/**
 * RFP Agent Help Content
 * 
 * Help articles specific to the RFP Agent plugin
 */

// Import types from HelpCenter - use direct path since types may not be re-exported
// @ts-ignore - Direct import path for types
import type { HelpArticleData, HelpCategory } from '@tracekit/core/src/components/HelpCenter/types';

export const rfpAgentHelpContent: HelpArticleData[] = [
  {
    id: 'rfp-getting-started',
    title: 'Getting Started with RFP Agent',
    category: 'getting-started' as HelpCategory,
    summary: 'Learn the basics of using RFP Agent for tender discovery and proposal generation',
    keywords: ['getting-started', 'overview', 'features', 'rfp-agent', 'tender', 'proposal'],
    lastUpdated: '2025-12-24',
    plugin: 'rfp-agent',
    priority: 100, // Highest priority
    subcategory: 'Getting Started',
    content: `
# Getting Started with RFP Agent

RFP Agent is an AI-powered system for discovering tender opportunities and generating proposals that transforms the proposal creation process from a manual, time-intensive workflow into an efficient, AI-enhanced system.

## Why RFP Agent?

### The Problem: Manual Proposal Creation

**Before RFP Agent**, proposal creation was a cumbersome, time-consuming process:

- **60 hours average** per RFP proposal
- **4-6 hours**: Manual RFP requirement extraction (reading 50-100 page PDFs)
- **2-3 hours**: Past proposal research (SharePoint search, file review)
- **20-30 hours**: Draft writing (from scratch or copy-paste from old proposals)
- **10-15 hours**: Review, editing, and word-count condensing
- **Limited capacity**: Only 40 RFPs/year due to staff bandwidth constraints
- **Manual citation management**: Time-consuming and error-prone
- **Inconsistent quality**: Varies by writer experience and available time

### The Solution: AI-Enhanced Proposal Creation

**With RFP Agent**, the process is dramatically streamlined:

- **30-50% time reduction**: From 60 hours to 36-42 hours per proposal
- **Automatic requirement extraction**: AI analyzes RFP documents in minutes
- **Intelligent content retrieval**: Search 15,000+ documents instantly
- **AI-powered generation**: Complete proposal drafts with citations in 2-5 minutes
- **Automatic citation management**: Every claim backed by source documents
- **Consistent quality**: Standardized structure and evidence-based content
- **Increased capacity**: 25% more opportunities evaluated (40 → 50 RFPs/year)

### Value & Impact

- **Time Savings**: Save 24 hours per RFP @ $181/hr = **$4,344 savings per proposal**
- **Capacity Increase**: 10 more opportunities evaluated per year
- **Quality Improvement**: Evidence-based proposals with full traceability
- **Compliance Assurance**: Built-in EGBC compliance checks
- **Knowledge Leverage**: Access to 15,000 documents, 90,000 entities, 200,000 searchable chunks

## Key Features

- **Multi-Source Opportunity Discovery**: Automatically sync opportunities from 15+ data sources including BC Bid, Bids&Tender, Bonfire, Merx, SAP Ariba, Jaggaer, and City Portals.

- **Intelligent Staff Matching**: Match staff members to RFP requirements based on designations, experience, location, and expertise areas.

- **Service Line Matching**: Automatically categorize opportunities by 9 service lines:
  - Contaminated Sites
  - Environmental Engineering
  - Biology & EIA
  - Indoor Air Quality
  - EHS Management
  - Spill Response
  - Landfill Management
  - Forestry
  - Indigenous Engagement

- **Geographic Filtering**: Filter opportunities by 55 cities across 6 provinces (BC, Ontario, Alberta, Manitoba, NWT, Yukon).

- **Deduplication**: Automatically identify and merge duplicate opportunities across sources.

## Quick Start

1. **Configure Data Sources**: Go to Integrations Dashboard to enable and configure data sources
2. **Sync Opportunities**: Click "Sync Now" on any data source to fetch latest opportunities
3. **Extract Requirements**: Upload an RFP document and extract requirements
4. **Match Staff**: Use Staff Matching Dashboard to find qualified staff members
5. **Generate Proposal**: Use the proposal generation workflow to create drafts

## Next Steps

- [Data Source Integrations](#rfp-data-sources) - Configure and manage 15+ data source integrations
- [Staff Matching System](#rfp-staff-matching) - Automatically match staff to RFP requirements
- [Service Lines](#rfp-service-lines) - Understand the 9 service lines and automatic categorization
    `,
  },
  {
    id: 'rfp-data-sources',
    title: 'Data Source Integrations',
    category: 'sources' as HelpCategory,
    summary: 'Configure and manage 15+ data source integrations for opportunity discovery',
    keywords: ['data-sources', 'integrations', 'bc-bid', 'bids-tender', 'bonfire', 'merx', 'sap-ariba', 'jaggaer'],
    lastUpdated: '2025-12-24',
    plugin: 'rfp-agent',
    content: `
# Data Source Integrations

RFP Agent supports 15+ data sources for opportunity discovery.

## Priority Data Sources

### 1. BC Bid (Priority 1)
- **84 opportunities** identified
- **12 BC cities** covered
- **Status**: Integrated
- **Authentication**: Required (credentials in Keepass)
- **Features**: Local staff requirements, competitive bidding

### 2. Bids&Tender (Priority 2)
- **49 opportunities** identified
- **13 cities** (BC and Ontario)
- **Status**: Integrated
- **Authentication**: Required (rrai@keystoneenvironmental.ca)
- **Features**: Budget constraints, capacity limitations

### 3. Bonfire (Priority 3)
- **20 opportunities** identified
- **5 BC cities**
- **Status**: Integrated
- **Authentication**: Required (aboons@keystoneenvironmental.ca)
- **Features**: Healthcare project experience, multi-phase projects

### 4. Merx (Priority 4)
- **12 opportunities** identified
- **Ontario-focused**
- **Status**: Integrated
- **Authentication**: Required (wdonald)
- **Features**: First Nations work, remote locations

### 5. SAP Ariba (Priority 5)
- **24 opportunities** identified
- **Federal and provincial**
- **Status**: Integrated
- **Authentication**: Required (multiple accounts)
- **Features**: Complex bid requirements, large-scale projects

### 6. Jaggaer (Priority 6)
- **5 opportunities** identified
- **Status**: Integrated
- **Authentication**: Required (multiple accounts)

### 7. City Portals (Priority 7)
- **8+ opportunities** identified
- **Cities**: Coquitlam, Chilliwack, Port Alberni, Surrey, Vancouver
- **Status**: Integrated
- **Features**: City-specific procurement portals

## Other Data Sources

- **Canada Grants**: Federal grants database
- **Canada Tenders**: Federal procurement opportunities
- **SAM.gov**: US Federal opportunities
- **World Bank**: International development opportunities
- **RSS Feeds**: News and industry updates

## Configuration

Each data source can be configured with:
- **API Keys**: Stored securely in environment variables
- **Sync Frequency**: Automatic or manual sync
- **Geographic Filters**: Filter by cities and provinces
- **Service Line Filters**: Filter by service lines
- **Keywords**: Custom keyword filtering

## Authentication

Credentials are stored in:
- Keepass database (BidsDB.kdbx)
- Environment variables (.env.local)
- Infrastructure credentials file

See [Credential Management](#rfp-credentials) for details.
    `,
  },
  {
    id: 'rfp-staff-matching',
    title: 'Staff Matching System',
    category: 'plugins' as HelpCategory,
    summary: 'Automatically match staff members to RFP requirements based on designations, experience, and location',
    keywords: ['staff-matching', 'matching', 'requirements', 'designations', 'p-eng', 'csap', 'qpesa'],
    lastUpdated: '2025-12-24',
    plugin: 'rfp-agent',
    content: `
# Staff Matching System

The Staff Matching system automatically matches staff members to RFP requirements.

## How It Works

1. **Extract Requirements**: System extracts staff requirements from RFP documents
2. **Evaluate Rules**: Matching rules are evaluated against staff database
3. **Calculate Scores**: Each staff member receives a score (0-100)
4. **Rank Results**: Staff members are ranked by match score

## Matching Criteria

### Designations
- **P.Eng** (Professional Engineer): 25 staff members
- **P.Geo** (Professional Geoscientist)
- **R.P.Bio** (Registered Professional Biologist)
- **CSAP** (Contaminated Sites Approved Professional): 10 staff members
- **QPESA** (Ontario-specific): Required for Ontario opportunities

### Experience
- Minimum years of experience
- Preferred years of experience
- Project history matching

### Location
- **BC**: Burnaby, Victoria offices
- **Ontario**: Mississauga office
- **Remote**: Fly-in communities, First Nations work

### Expertise Areas
- Service line matching
- Technical expertise
- Industry experience

## Matching Rules

Rules are configurable and include:
- **Category**: Designation, Experience, Location, Expertise
- **Priority**: High, Medium, Low
- **Weight**: Contribution to final score
- **Condition**: Matching logic
- **Score Calculation**: How points are awarded

## Using Staff Matching

1. Go to **Staff Matching Dashboard**
2. Enter RFP requirements or upload RFP document
3. Review matching results with scores and explanations
4. Select staff members for proposal team

## Best Practices

- Review top matches carefully
- Consider designation requirements (CSAP for BC, QPESA for Ontario)
- Check location requirements (local staff may be required)
- Verify availability and capacity
    `,
  },
  {
    id: 'rfp-service-lines',
    title: 'Service Lines',
    category: 'plugins' as HelpCategory,
    summary: 'Understand the 9 service lines and how opportunities are automatically categorized',
    keywords: ['service-lines', 'categories', 'filtering', 'contaminated-sites', 'environmental-engineering'],
    lastUpdated: '2025-12-24',
    plugin: 'rfp-agent',
    content: `
# Service Lines

RFP Agent categorizes opportunities by 9 service lines.

## Service Line Overview

### 1. Contaminated Sites
- **80+ opportunities** mapped
- **Key Designations**: CSAP, P.Eng, P.Geo
- **Capabilities**: Site assessment, remediation, risk assessment
- **Geographic Focus**: BC, Ontario

### 2. Environmental Engineering
- **50+ opportunities** mapped
- **Key Designations**: P.Eng
- **Capabilities**: Engineering design, permitting, compliance
- **Geographic Focus**: BC, Ontario, Alberta

### 3. Biology & EIA
- **40+ opportunities** mapped
- **Key Designations**: R.P.Bio, P.Biol
- **Capabilities**: Environmental impact assessment, wildlife surveys
- **Geographic Focus**: BC, Ontario

### 4. Indoor Air Quality
- **10+ opportunities** mapped
- **Key Designations**: CSAP, EP
- **Capabilities**: IAQ assessments, mold remediation
- **Geographic Focus**: BC (Sterling IAQ)

### 5. EHS Management
- **1+ opportunities** mapped
- **Key Designations**: EP, CSAP
- **Capabilities**: Environmental health and safety programs
- **Geographic Focus**: Canada-wide

### 6. Spill Response
- **4+ opportunities** mapped
- **Key Designations**: CSAP, P.Eng
- **Capabilities**: 24/7 spill response, emergency response
- **Geographic Focus**: Western Canada (BC, Alberta)

### 7. Landfill Management
- **5+ opportunities** mapped
- **Key Designations**: P.Eng, P.Geo
- **Capabilities**: Landfill design, monitoring, closure
- **Geographic Focus**: BC, Ontario

### 8. Forestry
- **1+ opportunities** mapped
- **Key Designations**: P.Ag, R.P.Bio
- **Capabilities**: Forestry services (limited - not SAFE certified)
- **Geographic Focus**: BC

### 9. Indigenous Engagement
- **1+ opportunities** mapped
- **Key Designations**: Various
- **Capabilities**: Indigenous consultation, community engagement
- **Geographic Focus**: Canada-wide

## Service Line Matching

Opportunities are automatically matched to service lines based on:
- **Keywords**: Extracted from title and description
- **NAICS Codes**: Industry classification codes
- **Category Tags**: Data source categories
- **Manual Override**: User can manually assign service lines

## Filtering by Service Line

You can filter opportunities by service line in:
- **Integrations Dashboard**: Filter sync results
- **Opportunities Dashboard**: Filter displayed opportunities
- **Search**: Include service line in search criteria
    `,
  },
  {
    id: 'rfp-geographic-filtering',
    title: 'Geographic Filtering',
    category: 'plugins' as HelpCategory,
    summary: 'Filter opportunities by 55 cities and 6 provinces across Canada',
    keywords: ['geographic', 'filtering', 'location', 'cities', 'provinces', 'bc', 'ontario'],
    lastUpdated: '2025-12-24',
    plugin: 'rfp-agent',
    content: `
# Geographic Filtering

Filter opportunities by location across 55 cities and 6 provinces.

## Provinces Supported

1. **British Columbia (BC)** - High Priority
   - Primary focus with 200+ opportunities
   - 12 priority cities: Vancouver, Surrey, Burnaby, Richmond, Victoria, Nanaimo, Kelowna, Chilliwack, Coquitlam, Delta, Kamloops, Prince George

2. **Ontario (ON)** - High Priority
   - 28 opportunities identified
   - 10 priority cities: Mississauga, Toronto, Barrie, Guelph, Kitchener, London, Oshawa, Pickering, Waterloo, Sarnia

3. **Alberta (AB)** - Medium Priority
   - Cities: Edmonton, Calgary

4. **Manitoba (MB)** - Medium Priority
   - City: Winnipeg

5. **Northwest Territories (NT)** - Low Priority
   - City: Yellowknife

6. **Yukon (YT)** - Low Priority
   - City: Whitehorse

## Regions

- **Metro Vancouver**: Vancouver, Surrey, Burnaby, Richmond, Coquitlam, Delta, North Vancouver, Pitt Meadows, Maple Ridge, Port Coquitlam
- **Capital Regional District**: Victoria, Colwood, Sooke
- **Fraser Valley**: Abbotsford, Chilliwack, Mission
- **Greater Toronto Area**: Toronto, Mississauga, Markham, Oakville, Ajax, Pickering

## Geographic Conditions

### Ontario-Specific Requirements
- **14 opportunities** require Ontario-specific qualifications
- **QPESA designation** required for many Ontario opportunities
- **Local staff** may be required

### BC-Specific Requirements
- **3 opportunities** require BC-specific qualifications
- **CSAP designation** required for contaminated sites work
- **Local staff** may be required for remote locations

### Remote Locations
- First Nations work
- Fly-in communities
- Remote mining sites
- Northern territories

## Using Geographic Filters

1. **In Integrations Dashboard**: Filter sync results by province/city
2. **In Opportunities Dashboard**: Filter displayed opportunities
3. **In Search**: Include location in search criteria

## Local Staff Requirements

Some opportunities require local staff presence:
- **Prince Rupert**: Requires local field personnel
- **Remote locations**: Fly-in communities, First Nations
- **Ontario opportunities**: May require Ontario-based staff

The system automatically checks if local staff is available for each opportunity.
    `,
  },
  {
    id: 'rfp-credentials',
    title: 'Credential Management',
    category: 'admin' as HelpCategory,
    summary: 'Secure storage and management of data source credentials',
    keywords: ['credentials', 'authentication', 'security', 'api-keys', 'keepass'],
    lastUpdated: '2025-12-24',
    plugin: 'rfp-agent',
    content: `
# Credential Management

Secure storage and management of data source credentials.

## Credential Storage

Credentials are stored in multiple locations for redundancy:

1. **Keepass Database** (BidsDB.kdbx)
   - Master password required
   - Contains all tender source credentials
   - Primary credential store

2. **Environment Variables** (.env.local)
   - API keys and tokens
   - Used by backend services
   - Not committed to version control

3. **Infrastructure Credentials File**
   - Backup credential store
   - Used for deployment

## Data Source Credentials

### BC Bid
- Username and password
- API keys (if available)

### Bids&Tender
- Username: rrai@keystoneenvironmental.ca
- Password
- API keys

### Bonfire
- Username: aboons@keystoneenvironmental.ca
- Password
- API keys

### Merx
- Username: wdonald
- Password
- API keys

### SAP Ariba
- Multiple accounts
- Passwords
- API keys

### Jaggaer
- Multiple accounts
- Passwords
- API keys

### City Portals
- Various city portal logins
- Identified per city

## Security Best Practices

- **Never commit credentials** to version control
- **Rotate credentials** regularly
- **Use environment variables** for API keys
- **Restrict access** to credential files
- **Use Keepass** for secure storage

## Updating Credentials

1. Update credentials in Keepass database
2. Update environment variables if needed
3. Update infrastructure credentials file
4. Test authentication for each data source
5. Document changes in credential mapping document

## Troubleshooting

If authentication fails:
1. Verify credentials in Keepass
2. Check environment variables
3. Verify API keys are valid
4. Check for credential expiration
5. Contact data source support if needed
    `,
  },
  {
    id: 'rfp-deduplication',
    title: 'Opportunity Deduplication',
    category: 'plugins' as HelpCategory,
    summary: 'How RFP Agent automatically identifies and merges duplicate opportunities',
    keywords: ['deduplication', 'duplicates', 'merging', 'matching'],
    lastUpdated: '2025-12-24',
    plugin: 'rfp-agent',
    content: `
# Opportunity Deduplication

RFP Agent automatically identifies and merges duplicate opportunities across data sources.

## How Deduplication Works

### 1. Exact Matching
- **Reference Number + Source**: Same reference number from same source
- **Highest Priority**: Exact matches are identified first

### 2. URL Matching
- **Normalized URLs**: URLs are normalized (remove query params, fragments)
- **Same URL = Duplicate**: Opportunities with same URL are considered duplicates

### 3. Similar Matching
- **Title Similarity**: 85%+ similarity threshold
- **Organization Match**: Same organization
- **Deadline Proximity**: Within 7 days
- **All three required**: Title similarity + organization + deadline proximity

## Deduplication Priority

1. **Exact duplicates** (reference_number + source) - highest priority
2. **URL duplicates** - high priority
3. **Similar duplicates** (title + organization + deadline) - medium priority

## Merging Duplicates

When duplicates are found:
- **Most complete data** is kept (based on completeness score)
- **Missing fields** are filled from duplicate
- **Source tracking** is maintained (metadata.merged_from)
- **Original sources** are preserved

## Completeness Score

Opportunities are scored on:
- Title (required)
- Description (50+ characters)
- Organization
- Deadline
- URL
- Contact information
- Amount/value
- Reference number

## Viewing Duplicates

Deduplication results include:
- **Unique opportunities**: Final deduplicated list
- **Duplicate information**: Which opportunities were duplicates
- **Match type**: Exact, URL, or similar
- **Similarity score**: For similar matches

## Best Practices

- Review duplicate matches for accuracy
- Adjust similarity threshold if needed (default: 85%)
- Verify merged data is complete
- Check source tracking for merged opportunities
    `,
  },
  {
    id: 'rfp-proposal-management-dashboard',
    title: 'Proposal Management Dashboard',
    category: 'plugins' as HelpCategory,
    summary: 'Unified dashboard for proposal and opportunity management with AI-powered features',
    keywords: ['proposal', 'dashboard', 'management', 'workflow', 'rfp', 'opportunity'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Proposal Management Dashboard

The Proposal Management Dashboard is a comprehensive proposal creation suite that integrates AI seamlessly at every step of the proposal lifecycle, from opportunity discovery to final submission.

## Overview

The dashboard provides a unified interface for managing all aspects of proposal creation and opportunity tracking. It combines multiple workflows into a single, intuitive interface with tab-based navigation.

## Dashboard Tabs

### 1. Proposals List
Browse all proposals in the system, view proposal status and metadata, filter by status, date, opportunity, and other criteria. Quick access to proposal details and editing.

### 2. Search
Advanced semantic search across all proposals. Search by content, metadata, or full-text. Filter results by multiple criteria and save searches for quick access.

### 3. Requirements (Coming Soon)
Automated requirement extraction from RFPs, requirement analysis and compliance tracking, requirement-to-capability mapping, and compliance matrix generation.

### 4. Opportunity Matching (Coming Soon)
AI-powered opportunity-to-capability matching, match score calculation and ranking, feasibility assessment and recommendations, and opportunity prioritization.

### 5. Pipeline
Track opportunities through proposal pipeline stages, monitor proposal progress and status, manage proposal workflows, and track key milestones and deadlines.

### 6. Review
Senior review workflow management, reviewer assignment and tracking, comment and approval system, and version control and change tracking.

## Key Features

- **AI Integration**: AI-powered features at every step including keyword suggestions, opportunity summaries, feasibility assessment, template generation, proposal generation, section editing, and intelligent rewriting
- **Citation System**: All AI outputs cite and reference the most relevant of 15,000 internal documents
- **Knowledge Base**: Leverages 90,000 structured entities and 200,000 searchable chunks from 15,000 documents
- **Traceability**: Full traceability from proposal content to source documents with document-level and page-level citations

## Quick Start

1. Navigate to **Proposal Management Dashboard** from the main menu
2. Select the appropriate tab for your task (Proposals, Search, Pipeline, or Review)
3. Use AI-powered features to enhance your workflow
4. Review citations and source documents for all generated content

## Related Articles

- [Proposals List Tab](#rfp-proposal-list-tab)
- [Proposal Search Tab](#rfp-proposal-search-tab)
- [Pipeline Tab](#rfp-pipeline-tab)
- [Review Workflow Tab](#rfp-review-workflow-tab)
- [AI-Powered Proposal Generation](#rfp-ai-proposal-generation)
    `,
  },
  {
    id: 'rfp-proposal-list-tab',
    title: 'Proposals List Tab',
    category: 'plugins' as HelpCategory,
    summary: 'Browse and manage all proposals with filtering, status tracking, and quick actions',
    keywords: ['proposals', 'list', 'browse', 'filter', 'status', 'management'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Proposals List Tab

The Proposals List tab provides a comprehensive view of all proposals in the system, allowing you to browse, filter, and manage proposals efficiently.

## Features

### Proposal Browsing
- View all proposals in a unified list
- See proposal status, metadata, and key information at a glance
- Quick access to proposal details and editing

### Filtering Options
Filter proposals by:
- **Status**: Draft, In Review, Approved, Submitted, Won, Lost
- **Date**: Created date, submission date, deadline
- **Opportunity**: Associated opportunity or RFP
- **Client**: Client name or organization
- **Service Line**: Contaminated Sites, Environmental Engineering, etc.
- **Win Status**: Won, Lost, Pending, Unknown

### Proposal Cards
Each proposal card displays:
- Proposal title and client name
- Status badge with color coding
- Submission date or deadline
- Service line and project type
- Quick action buttons (View, Edit, Delete)

### Quick Actions
- **View Proposal**: Open proposal in detail view
- **Edit Proposal**: Navigate to proposal editor
- **Generate New**: Start new proposal from opportunity
- **Export**: Download proposal as PDF or DOCX
- **Share**: Share proposal with team members

## Using the Proposals List

1. Navigate to **Proposal Management Dashboard** > **Proposals** tab
2. Use filters to narrow down proposals
3. Click on a proposal card to view details
4. Use quick actions for common tasks

## Status Indicators

- **Draft**: Proposal is being created or edited
- **In Review**: Proposal is awaiting senior review
- **Approved**: Proposal has been approved for submission
- **Submitted**: Proposal has been submitted to client
- **Won**: Proposal resulted in a won contract
- **Lost**: Proposal did not result in a contract

## Best Practices

- Use filters to focus on proposals requiring attention
- Regularly review proposals in "In Review" status
- Update proposal status as workflow progresses
- Use quick actions for efficient proposal management

## Related Articles

- [Proposal Management Dashboard](#rfp-proposal-management-dashboard)
- [Proposal Search Tab](#rfp-proposal-search-tab)
- [Review Workflow Tab](#rfp-review-workflow-tab)
    `,
  },
  {
    id: 'rfp-proposal-search-tab',
    title: 'Proposal Search Tab',
    category: 'plugins' as HelpCategory,
    summary: 'Advanced semantic search across proposals with entity-based queries and comparison features',
    keywords: ['search', 'proposals', 'semantic', 'entity', 'comparison', 'evidence'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Proposal Search Tab

The Proposal Search tab provides advanced semantic search capabilities across all proposals, enabling you to find relevant content, compare proposals, and build reusable content libraries.

## Search Features

### Semantic Search
- Search across proposal content using natural language queries
- AI-powered relevance ranking
- Context-aware results

### Entity-Based Queries
- Search by proposal entities (proposal_document entity type)
- Filter by metadata fields (client, service line, win status, etc.)
- Query structured data efficiently

### View Modes
- **Grid View**: Card-based layout for visual browsing
- **List View**: Compact list with key information
- **Comparison View**: Side-by-side comparison of selected proposals

## Filtering Options

### Date Range
- Filter by submission date range
- Filter by creation date
- Filter by deadline

### Client Filter
- Filter by client name
- Filter by organization type
- Filter by geographic location

### Project Type
- Filter by project type
- Filter by service line
- Filter by contract value range

### Win Status
- Filter by win/loss status
- Filter by pending proposals
- Filter by unknown outcomes

### Service Line
- Filter by specific service lines
- Filter by multiple service lines
- Filter by service line combinations

## Proposal Comparison

### Side-by-Side Comparison
- Select multiple proposals for comparison
- Compare key sections and content
- Identify best practices and patterns

### Reusable Content Library
- Save frequently used sections
- Build library of proven content
- Reuse content across proposals

### Win/Loss Analysis
- Analyze winning proposals
- Identify success factors
- Learn from lost proposals

## Evidence Basket Integration

- Add proposals to evidence basket
- Collect evidence from multiple proposals
- Use evidence in new proposal generation

## Search Results

Each search result displays:
- Proposal title and client
- Key metadata (date, service line, win status)
- Relevance score
- Quick preview of content
- Actions (View, Add to Basket, Compare)

## Using Proposal Search

1. Navigate to **Proposal Management Dashboard** > **Search** tab
2. Enter search query or use filters
3. Review results in your preferred view mode
4. Select proposals for comparison or add to evidence basket
5. Use saved searches for quick access

## Best Practices

- Use specific keywords for better results
- Combine filters for precise searches
- Save common searches for efficiency
- Use comparison view to identify patterns
- Build evidence basket for proposal generation

## Related Articles

- [Proposal Management Dashboard](#rfp-proposal-management-dashboard)
- [Evidence Basket System](#rfp-evidence-basket-system)
- [AI-Powered Proposal Generation](#rfp-ai-proposal-generation)
    `,
  },
  {
    id: 'rfp-pipeline-tab',
    title: 'Pipeline Tab',
    category: 'plugins' as HelpCategory,
    summary: 'Track opportunities through pipeline stages with visual pipeline, metrics, and forecasting',
    keywords: ['pipeline', 'stages', 'tracking', 'forecasting', 'metrics', 'conversion'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Pipeline Tab

The Pipeline tab provides visual pipeline tracking with stage management, velocity metrics, conversion rate analysis, and revenue forecasting.

## Pipeline Stages

### Stage Overview
1. **Lead**: Initial opportunity identified
2. **Qualified**: Opportunity qualified for pursuit
3. **Proposal**: Proposal in development or submitted
4. **Negotiation**: Contract negotiation in progress
5. **Won**: Contract awarded
6. **Lost**: Opportunity lost or declined

## Visual Pipeline

### Kanban Board View
- Drag-and-drop opportunities between stages
- Visual representation of pipeline health
- Color-coded by status and priority

### Stage Metrics
Each stage displays:
- Number of opportunities
- Total pipeline value
- Weighted forecast value
- Average days in stage
- Conversion rate to next stage

## Pipeline Metrics

### Total Pipeline Value
- Sum of all opportunity values in pipeline
- Weighted by win probability
- Forecasted revenue

### Conversion Rates
- Stage-to-stage conversion rates
- Overall pipeline conversion rate
- Historical conversion trends

### Velocity Metrics
- Average days in each stage
- Time from lead to won
- Bottleneck identification

### Win Probability
- AI-calculated win probability per opportunity
- Weighted forecast calculation
- Probability-based revenue forecasting

## Bottleneck Identification

The system automatically identifies:
- Stages with longest average time
- Stages with low conversion rates
- Opportunities stuck in stages
- Resource constraints

## Revenue Forecasting

### Forecast Calculation
- Weighted by win probability
- Time-based forecasting
- Monthly forecast breakdown

### Forecast Accuracy
- Historical accuracy tracking
- Forecast vs. actual comparison
- Confidence intervals

## Using the Pipeline

1. Navigate to **Proposal Management Dashboard** > **Pipeline** tab
2. Review visual pipeline and stage metrics
3. Move opportunities between stages as they progress
4. Monitor conversion rates and bottlenecks
5. Review revenue forecasts

## Best Practices

- Regularly update opportunity stages
- Monitor conversion rates for improvement opportunities
- Address bottlenecks promptly
- Use forecasts for resource planning
- Review win/loss patterns

## Related Articles

- [Proposal Management Dashboard](#rfp-proposal-management-dashboard)
- [AI-Powered Feasibility Assessment](#rfp-ai-feasibility-assessment)
    `,
  },
  {
    id: 'rfp-review-workflow-tab',
    title: 'Review Workflow Tab',
    category: 'plugins' as HelpCategory,
    summary: 'Senior review workflow with RFP-specific checklist, comments, and approval system',
    keywords: ['review', 'workflow', 'approval', 'checklist', 'senior', 'quality'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Review Workflow Tab

The Review Workflow tab manages senior review processes for proposals, including reviewer assignment, RFP-specific checklists, comment systems, and approval workflows.

## Review Process

### Proposal Selection
- View proposals awaiting review
- Filter by status, reviewer, or date
- Access proposal details and content

### Reviewer Assignment
- Assign reviewers based on role (P.Geo, P.Eng)
- Multiple reviewers per proposal
- Reviewer notifications

### Review Status
- **Draft**: Proposal being created
- **In Review**: Awaiting reviewer feedback
- **Approved**: Approved for submission
- **Rejected**: Requires revisions

## RFP-Specific Checklist

### Checklist Items
- Compliance with RFP requirements
- Technical accuracy and completeness
- Citation coverage and accuracy
- Quality and professionalism
- Client-specific requirements

### Checklist Management
- Check/uncheck items as reviewed
- Add notes to checklist items
- Track completion status
- Require all items for approval

## Comment System

### Comments
- Add comments to specific sections
- Reply to comments
- Resolve comments
- Comment threads

### Feedback
- General feedback notes
- Revision requests
- Approval notes
- Rejection reasons

## Approval Workflow

### Approval Process
1. Proposal submitted for review
2. Reviewers assigned
3. Checklist completed
4. Comments addressed
5. Approval or rejection decision

### Version Control
- Track proposal versions
- View change history
- Compare versions
- Restore previous versions

## Using Review Workflow

1. Navigate to **Proposal Management Dashboard** > **Review** tab
2. Select proposal for review
3. Complete RFP-specific checklist
4. Add comments and feedback
5. Approve or request revisions

## Best Practices

- Complete checklist thoroughly
- Provide specific, actionable feedback
- Address all comments before approval
- Maintain version history
- Document approval decisions

## Related Articles

- [Proposal Management Dashboard](#rfp-proposal-management-dashboard)
- [Review Checklist System](#rfp-review-checklist-system)
    `,
  },
  {
    id: 'rfp-ai-keyword-suggestions',
    title: 'AI-Powered Keyword Suggestions',
    category: 'plugins' as HelpCategory,
    summary: 'Intelligent keyword suggestions for opportunity discovery and content search',
    keywords: ['ai', 'keywords', 'suggestions', 'search', 'discovery'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# AI-Powered Keyword Suggestions

The system provides intelligent keyword suggestions to help users discover relevant opportunities and content more effectively.

## Features

### Context-Aware Suggestions
- AI analyzes user's search history
- Considers current context and filters
- Suggests relevant terms based on activity

### Opportunity-Based Suggestions
- Suggests keywords based on opportunity requirements
- Matches keywords to service lines
- Identifies relevant capabilities

### Knowledge Base Insights
- Leverages 15,000 documents for suggestions
- Uses 90,000 entities for keyword extraction
- Suggests terms from past proposals

### Multi-Language Support
- Supports English keyword suggestions
- Supports French keyword suggestions
- Language-aware suggestions

## How It Works

1. User starts typing in search field
2. System analyzes context and history
3. AI generates relevant keyword suggestions
4. Suggestions appear in dropdown
5. User clicks to apply suggestion

## Using Keyword Suggestions

1. Start typing in search or filter field
2. Review suggested keywords
3. Click suggestion to apply
4. Refine search with additional keywords

## Best Practices

- Use suggestions to discover new opportunities
- Combine multiple keyword suggestions
- Review suggested terms for relevance
- Use suggestions to refine searches

## Related Articles

- [Proposal Search Tab](#rfp-proposal-search-tab)
- [Government Data Dashboard](#rfp-government-data-dashboard)
    `,
  },
  {
    id: 'rfp-ai-opportunity-summarization',
    title: 'AI-Powered Opportunity Summarization',
    category: 'plugins' as HelpCategory,
    summary: 'Automatic comprehensive summaries of government opportunities with key information extraction',
    keywords: ['ai', 'summarization', 'opportunity', 'summary', 'key-points'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# AI-Powered Opportunity Summarization

AI automatically generates comprehensive summaries of government opportunities, extracting key information and presenting it in an easily digestible format.

## Summary Features

### Key Points Extraction
- Identifies and highlights critical opportunity details
- Extracts requirements and deliverables
- Highlights deadlines and important dates

### Relevant Capabilities
- Matches opportunity requirements to Keystone capabilities
- Identifies relevant past projects
- Highlights service line matches

### Match Confidence
- Calculates and displays confidence scores
- Indicates strength of capability match
- Provides match reasoning

### Structured Format
- Organized summary with clear sections
- Easy-to-scan format
- Key information highlighted

## Summary Content

Each summary includes:
- **Opportunity Overview**: Title, organization, objectives
- **Key Requirements**: Mandatory and preferred requirements
- **Timeline**: Deadlines, submission dates, project timeline
- **Budget**: Contract value, budget information
- **Agency Information**: Contact details, agency background
- **Relevant Capabilities**: Keystone capabilities and past projects
- **Feasibility Indicators**: Match scores, recommendations

## Generation Process

1. System analyzes opportunity document
2. AI extracts key information
3. Matches requirements to capabilities
4. Generates structured summary
5. Displays summary with confidence scores

## Using Opportunity Summaries

1. Navigate to opportunity in dashboard
2. View AI-generated summary
3. Review key points and requirements
4. Check capability matches
5. Use summary for decision-making

## Performance

- **Generation Time**: <5 seconds for typical opportunities
- **Accuracy**: High-quality summaries with relevant information
- **Coverage**: Comprehensive coverage of opportunity details

## Best Practices

- Review summaries before reading full opportunity
- Use summaries for quick opportunity assessment
- Check match confidence scores
- Verify key requirements in full document

## Related Articles

- [Government Data Dashboard](#rfp-government-data-dashboard)
- [AI-Powered Feasibility Assessment](#rfp-ai-feasibility-assessment)
    `,
  },
  {
    id: 'rfp-ai-feasibility-assessment',
    title: 'AI-Powered Feasibility Assessment',
    category: 'plugins' as HelpCategory,
    summary: 'Intelligent feasibility assessment analyzing requirements against capabilities and past performance',
    keywords: ['ai', 'feasibility', 'assessment', 'requirements', 'capabilities', 'scoring'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# AI-Powered Feasibility Assessment

The system provides intelligent feasibility assessment for opportunities, analyzing requirements against Keystone's capabilities and past performance.

## Assessment Components

### Requirement Analysis
- Extracts and analyzes all opportunity requirements
- Identifies mandatory vs. optional requirements
- Assesses complexity and scope

### Capability Matching
- Matches requirements to Keystone capabilities
- Identifies relevant past projects and experience
- Highlights strengths and potential gaps

### Feasibility Scoring
- Calculates overall feasibility score (0-100%)
- Provides detailed scoring breakdown
- Identifies risk factors and considerations

### Recommendations
- Provides go/no-go recommendations
- Suggests areas requiring additional information
- Recommends partnership opportunities if needed

## Output Format

### Overall Score
- Feasibility score (0-100%)
- Color-coded indicators (Green/Yellow/Red)
- Confidence level

### Detailed Analysis
- Requirement-by-requirement analysis
- Capability match summary
- Risk assessment and mitigation strategies
- Recommendations and next steps

## Using Feasibility Assessment

1. Navigate to opportunity in dashboard
2. Click "Assess Feasibility" or view in opportunity summary
3. Review overall feasibility score
4. Examine detailed requirement analysis
5. Review recommendations and next steps

## Best Practices

- Review detailed analysis, not just score
- Consider all risk factors
- Address identified gaps before pursuing
- Use recommendations for decision-making

## Related Articles

- [AI-Powered Opportunity Summarization](#rfp-ai-opportunity-summarization)
- [Proposal Management Dashboard](#rfp-proposal-management-dashboard)
    `,
  },
  {
    id: 'rfp-ai-template-generation',
    title: 'AI-Powered Template Generation',
    category: 'plugins' as HelpCategory,
    summary: 'Automatically generate proposal templates tailored to specific opportunities with requirement extraction',
    keywords: ['ai', 'template', 'generation', 'requirements', 'sections', 'questions'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# AI-Powered Template Generation

The system automatically generates proposal templates tailored to specific opportunities, extracting requirements and creating structured templates with relevant questions and sections.

## Template Generation Process

### Requirement Extraction
- Analyzes opportunity documents using RAG (Retrieval-Augmented Generation)
- Extracts all requirements and evaluation criteria
- Identifies mandatory sections and questions

### Template Structure
- Creates section-based template structure
- Generates relevant questions for each section
- Maps questions to entity types for evidence retrieval
- Includes default questions from policy pack

### Evidence Integration
- Identifies relevant evidence sources from knowledge base
- Suggests past proposals and documents for reference
- Maps questions to available evidence
- Provides citation suggestions

## Template Features

### Customized Sections
- Tailored to opportunity requirements
- Organized by evaluation criteria
- Includes mandatory and optional sections

### Relevant Questions
- AI-generated questions specific to the opportunity
- Based on RFP requirements and evaluation criteria
- Mapped to evidence sources

### Evidence Mapping
- Automatic mapping to relevant documents
- Suggests past proposals for reference
- Provides citation guidance

### Policy Compliance
- Ensures compliance with Keystone standards
- Includes default questions from policy pack
- Validates against organizational requirements

## Generation Process

1. Upload or select opportunity document
2. System analyzes requirements using RAG
3. Extracts requirements and evaluation criteria
4. Generates template structure and questions
5. Maps questions to evidence sources
6. Template ready for proposal creation

## Performance

- **Generation Time**: 10-30 seconds depending on opportunity complexity
- **Template Quality**: High-quality, opportunity-specific templates
- **Coverage**: Comprehensive coverage of all opportunity requirements

## Using Template Generation

1. Navigate to opportunity in dashboard
2. Click "Generate Template"
3. Wait for template generation (streaming progress updates)
4. Review generated template structure
5. Start filling in template sections

## Best Practices

- Review generated template before starting
- Customize questions as needed
- Use suggested evidence sources
- Ensure all mandatory sections are included

## Related Articles

- [AI-Powered Proposal Generation](#rfp-ai-proposal-generation)
- [Proposal Management Dashboard](#rfp-proposal-management-dashboard)
    `,
  },
  {
    id: 'rfp-ai-proposal-generation',
    title: 'AI-Powered Proposal Generation',
    category: 'plugins' as HelpCategory,
    summary: 'Generate complete proposal drafts using AI with evidence retrieval, citation integration, and quality assurance',
    keywords: ['ai', 'proposal', 'generation', 'evidence', 'citations', 'quality'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# AI-Powered Proposal Generation

The system generates complete proposal drafts using AI, leveraging the knowledge base of 15,000 documents to create accurate, well-cited proposals.

## Generation Process

### Evidence Retrieval
- Retrieves relevant documents from knowledge base
- Uses hybrid search (vector + keyword) for optimal results
- Filters by relevance score to ensure high-quality sources
- Retrieves up to 150 evidence sources for comprehensive queries

### Content Generation
- Generates proposal sections using retrieved evidence
- Ensures all claims are supported by source documents
- Maintains consistent tone and style
- Follows Keystone's proposal standards

### Citation Integration
- Automatically cites all referenced documents
- Includes page-level citations for precise attribution
- Maintains citation format consistency
- Provides traceability from proposal to source documents

### Quality Assurance
- Validates content against opportunity requirements
- Checks for completeness and compliance
- Ensures proper citation coverage
- Provides quality scores and recommendations

## Generation Features

### Multi-Section Generation
- Generates all proposal sections
- Maintains section structure and flow
- Ensures consistency across sections

### Evidence-Based Content
- All content backed by source documents
- Minimizes hallucination through evidence enforcement
- Provides source traceability

### Automatic Citations
- Citations included automatically
- Document-level and page-level citations
- Clickable links to source documents

### Progress Tracking
- Real-time progress updates during generation
- Section-by-section completion status
- Estimated time remaining

### Streaming Output
- Progressive content delivery for faster user feedback
- See content as it's generated
- Can stop generation if needed

## Performance

- **Generation Time**: 2-5 minutes for complete proposals
- **Citation Coverage**: 100% of claims supported by citations
- **Accuracy**: High accuracy with minimal hallucination
- **Traceability**: Full traceability from proposal to source documents

## Using Proposal Generation

1. Navigate to opportunity or use Proposal Builder
2. Click "Generate Proposal" or "Start Generation"
3. Monitor progress and streaming output
4. Review generated content and citations
5. Edit and refine as needed

## Best Practices

- Review all generated content carefully
- Verify citations are accurate
- Edit for tone and style as needed
- Ensure all requirements are addressed
- Use quality scores for improvement

## Related Articles

- [AI-Powered Template Generation](#rfp-ai-template-generation)
- [AI-Powered Section Editing](#rfp-ai-section-editing)
- [Citation & Reference System](#rfp-citation-reference-system)
    `,
  },
  {
    id: 'rfp-ai-section-editing',
    title: 'AI-Powered Section Editing & Rewriting',
    category: 'plugins' as HelpCategory,
    summary: 'Intelligent editing and rewriting capabilities for proposal sections with citation management',
    keywords: ['ai', 'editing', 'rewriting', 'sections', 'citations', 'quality'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# AI-Powered Section Editing & Rewriting

The system provides intelligent editing and rewriting capabilities for proposal sections, enabling users to refine and improve content while maintaining accuracy and citations.

## Editing Features

### Section Analysis
- Analyzes section content for quality and compliance
- Identifies areas for improvement
- Suggests enhancements based on opportunity requirements
- Checks citation coverage and accuracy

### Intelligent Rewriting
- Rewrites sections while preserving meaning and citations
- Improves clarity, tone, and professionalism
- Ensures compliance with opportunity requirements
- Maintains or enhances citation coverage

### Content Enhancement
- Adds relevant details from knowledge base
- Strengthens arguments with additional evidence
- Improves technical depth and accuracy
- Enhances competitive positioning

### Citation Management
- Updates citations when content changes
- Adds new citations for new claims
- Validates citation accuracy
- Maintains citation format consistency

## Editing Modes

### Quick Edit
- Fast improvements with minimal changes
- Preserves existing structure
- Quick citation updates

### Comprehensive Rewrite
- Complete section rewrite with enhancements
- Improved structure and flow
- Enhanced evidence integration

### Technical Enhancement
- Focus on technical depth and accuracy
- Strengthens technical arguments
- Adds technical evidence

### Compliance Review
- Ensure full compliance with requirements
- Addresses compliance gaps
- Validates against evaluation criteria

## User Experience

### Side-by-Side Comparison
- View original and edited versions
- See changes highlighted
- Compare citation changes

### Change Highlighting
- Visual indicators for changes
- Added content highlighted
- Removed content marked

### Citation Tracking
- See citation changes and additions
- Verify citation accuracy
- Track source usage

### Accept/Reject
- Easy approval workflow for changes
- Accept all or individual changes
- Reject unwanted modifications

## Using Section Editing

1. Navigate to proposal section
2. Click "Edit" or "Rewrite" button
3. Select editing mode (Quick Edit, Comprehensive Rewrite, etc.)
4. Review suggested changes
5. Accept or reject changes
6. Verify citations are updated

## Best Practices

- Use appropriate editing mode for your needs
- Review all changes before accepting
- Verify citations are accurate after editing
- Maintain consistency across sections
- Use compliance review mode before submission

## Related Articles

- [AI-Powered Proposal Generation](#rfp-ai-proposal-generation)
- [Citation & Reference System](#rfp-citation-reference-system)
    `,
  },
  {
    id: 'rfp-government-data-dashboard',
    title: 'Government Data Dashboard',
    category: 'plugins' as HelpCategory,
    summary: 'Unified dashboard for monitoring and managing all government data sources with zone management',
    keywords: ['government', 'data', 'dashboard', 'opportunities', 'integrations', 'zones'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Government Data Dashboard

The Government Data Dashboard provides a unified interface for monitoring and managing all government data sources, displaying tenders, opportunities, awards, and industry news in a single, intuitive interface.

## Overview

The dashboard combines multiple data sources and workflows into a unified interface with zone-based management for Canada, USA, and Global data sources.

## Dashboard Tabs

### 1. Opportunities
Browse all government opportunities with advanced filtering, search capabilities, and detailed opportunity information.

### 2. Industry News
Monitor RSS feeds for industry news, Federal Register alerts, and Executive Orders with auto-refresh capabilities.

### 3. Data Integrations
View all connected data sources, monitor integration health and sync status, configure data source settings, and manage sync schedules.

### 4. Awards (Coming Soon)
View contract awards and spending data, competitive intelligence on awarded contracts, and track award patterns and trends.

## Zone Management

### Zone Switching
- **Canada**: Canadian data sources (BC Bid, Bids&Tender, Merx, etc.)
- **USA**: US data sources (SAM.gov, Federal Register, Grants.gov, etc.)
- **Global**: International sources (World Bank, etc.)

### Zone-Specific Features
- Zone-based data source filtering
- Zone-specific keyword filtering
- Zone-based opportunity display

## Key Features

- **Unified Interface**: Single dashboard for all external data
- **Real-Time Updates**: Live sync status and last update timestamps
- **Advanced Filtering**: Multiple filter options for precise data discovery
- **Export Capabilities**: Export opportunities and awards for external analysis
- **Zone Support**: Filter by geographic zones (Canada, USA, Global)

## Quick Start

1. Navigate to **Government Data Dashboard** from main menu
2. Select zone (Canada, USA, or Global)
3. Choose appropriate tab (Opportunities, News, or Integrations)
4. Use filters and search to find relevant data
5. Sync data sources as needed

## Related Articles

- [Opportunities Tab](#rfp-opportunities-tab)
- [Industry News Tab](#rfp-news-tab)
- [Data Integrations Tab](#rfp-integrations-tab)
- [Zone Management System](#rfp-zone-management)
    `,
  },
  {
    id: 'rfp-opportunities-tab',
    title: 'Opportunities Tab',
    category: 'plugins' as HelpCategory,
    summary: 'Browse and search government procurement opportunities with entity-based queries and advanced filtering',
    keywords: ['opportunities', 'government', 'procurement', 'tenders', 'search', 'filter'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Opportunities Tab

The Opportunities tab provides comprehensive browsing and search capabilities for government procurement opportunities with entity-based queries, status tracking, and advanced filtering.

## Features

### Entity-Based Queries
- Query opportunities using govt_opportunity entity type
- Server-side pagination for large datasets
- Efficient filtering and sorting

### Status Tracking
- Track opportunities by status (watching, bidding, won, lost, expired)
- Update status as opportunities progress
- Filter by status

### Deadline Calendar
- Calendar view of opportunity deadlines
- Deadline notifications
- Time-based filtering

### Auto-Matching
- Automatically match opportunities to company capabilities
- Match score calculation
- Service line matching

### BI Analytics
- Win rate analysis
- Pipeline value tracking
- Time-to-bid metrics

### View Modes
- **Cards View**: Visual card-based layout
- **List View**: Compact list with key information
- **Calendar View**: Deadline-based calendar

## Filtering Options

### Source Filter
- Filter by data source (BC Bid, SAM.gov, etc.)
- Multiple source selection
- Source-specific filtering

### Status Filter
- Filter by tracking status
- Filter by opportunity status
- Combined status filtering

### Service Line Filter
- Filter by service line
- Multiple service line selection
- Service line matching

### Geographic Filter
- Filter by province/state
- Filter by city
- Geographic region filtering

## Search Capabilities

### Semantic Search
- Natural language search queries
- AI-powered relevance ranking
- Context-aware results

### Keyword Search
- Search in titles and descriptions
- Keyword highlighting
- Search history

## Using Opportunities Tab

1. Navigate to **Government Data Dashboard** > **Opportunities** tab
2. Select zone (Canada, USA, or Global)
3. Use filters and search to find opportunities
4. View opportunities in preferred view mode
5. Click opportunity for detailed view

## Best Practices

- Use filters to narrow down opportunities
- Track opportunities of interest
- Review match scores for relevance
- Monitor deadlines in calendar view
- Export opportunities for external analysis

## Related Articles

- [Government Data Dashboard](#rfp-government-data-dashboard)
- [Service Line Filtering](#rfp-service-line-filtering)
- [Geographic Filtering](#rfp-geographic-filtering)
    `,
  },
  {
    id: 'rfp-news-tab',
    title: 'Industry News Tab',
    category: 'plugins' as HelpCategory,
    summary: 'Monitor industry news, Federal Register alerts, and Executive Orders with RSS feed management',
    keywords: ['news', 'rss', 'federal-register', 'executive-orders', 'alerts', 'industry'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Industry News Tab

The Industry News tab monitors RSS feeds for industry news, Federal Register alerts, and Executive Orders with auto-refresh capabilities and keyword filtering.

## Features

### RSS Feed Monitoring
- Monitor multiple RSS feeds
- Feed selection and management
- Priority-based feed filtering
- Auto-refresh intervals

### Federal Register Alerts
- Monitor Federal Register documents
- Keyword-based filtering
- Document type filtering
- Auto-refresh capabilities

### Executive Orders
- Track Executive Orders
- Filter by year and president
- Document details and summaries
- Historical tracking

### Auto-Refresh
- Automatic content refresh
- Configurable refresh intervals
- Manual refresh option
- Refresh status indicators

## Feed Management

### Feed Selection
- Select active RSS feeds
- Priority-based feed display
- Feed health monitoring
- Feed configuration

### Keyword Filtering
- Filter news by keywords
- Zone-specific keyword filtering
- Keyword-based relevance ranking
- Saved keyword sets

## Using News Tab

1. Navigate to **Government Data Dashboard** > **Industry News** tab
2. Select zone (USA for Federal Register/Executive Orders)
3. Configure RSS feed selection
4. Set keyword filters
5. Enable auto-refresh if desired
6. Review news items and alerts

## Best Practices

- Select relevant RSS feeds for your needs
- Use keywords to filter relevant news
- Monitor Federal Register for regulatory changes
- Track Executive Orders for policy changes
- Enable auto-refresh for real-time updates

## Related Articles

- [Government Data Dashboard](#rfp-government-data-dashboard)
- [RSS Feed Management](#rfp-rss-feed-management)
- [US Government Integrations](#rfp-us-government-integrations)
    `,
  },
  {
    id: 'rfp-integrations-tab',
    title: 'Data Integrations Tab',
    category: 'plugins' as HelpCategory,
    summary: 'Manage data source integrations with health monitoring, sync controls, and configuration',
    keywords: ['integrations', 'data-sources', 'sync', 'health', 'configuration', 'credentials'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Data Integrations Tab

The Data Integrations tab displays and manages data source integrations with health monitoring, sync controls, configuration management, and credential management.

## Features

### Integration Management
- View all connected data sources
- Enable/disable integrations
- Configure integration settings
- Monitor integration health

### Health Monitoring
- Real-time health check status
- Last sync timestamp
- Sync success/failure indicators
- Error reporting

### Sync Controls
- Manual sync trigger
- Scheduled sync configuration
- Sync frequency settings
- Sync history viewing

### Configuration Management
- Geographic filter configuration
- Service line filter configuration
- Keyword filter configuration
- Sync schedule configuration

### Credential Management
- Secure credential storage
- Credential update interface
- Authentication testing
- Credential rotation

## Data Source Status

Each data source displays:
- **Status**: Enabled/Disabled
- **Health**: Healthy/Unhealthy/Unknown
- **Last Sync**: Timestamp of last successful sync
- **Opportunities Synced**: Count of synced opportunities
- **Sync Status**: Success/Failure/Pending

## Using Integrations Tab

1. Navigate to **Government Data Dashboard** > **Data Integrations** tab
2. Review integration status and health
3. Configure filters and sync settings
4. Trigger manual sync if needed
5. Update credentials as required

## Best Practices

- Monitor integration health regularly
- Configure appropriate sync frequencies
- Use geographic and service line filters
- Keep credentials up to date
- Review sync history for issues

## Related Articles

- [Government Data Dashboard](#rfp-government-data-dashboard)
- [Data Source Integrations](#rfp-data-source-integrations)
- [Sync Management](#rfp-sync-management)
- [Credential Management](#rfp-credentials)
    `,
  },
  {
    id: 'rfp-zone-management',
    title: 'Zone Management System',
    category: 'plugins' as HelpCategory,
    summary: 'Manage geographic zones (Canada, USA, Global) for data source filtering and organization',
    keywords: ['zones', 'geographic', 'canada', 'usa', 'global', 'filtering'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Zone Management System

The Zone Management system organizes data sources and opportunities by geographic zones (Canada, USA, Global) for efficient filtering and data organization.

## Zones

### Canada Zone
- Canadian data sources (BC Bid, Bids&Tender, Merx, etc.)
- Canadian opportunities and tenders
- Province and city filtering
- Canadian-specific requirements

### USA Zone
- US data sources (SAM.gov, Federal Register, Grants.gov, etc.)
- US opportunities and contracts
- State and city filtering
- US-specific requirements

### Global Zone
- International sources (World Bank, etc.)
- Global opportunities
- International filtering
- Cross-border requirements

## Zone Features

### Zone Switching
- Switch between zones in dashboard
- Zone-specific data display
- Zone-based filtering
- Zone-specific configurations

### Zone-Specific Data Sources
- Data sources organized by zone
- Zone-based source filtering
- Zone-specific sync settings
- Zone-based health monitoring

### Zone-Based Filtering
- Filter opportunities by zone
- Zone-specific keyword filtering
- Zone-based service line filtering
- Zone-specific geographic filtering

## Using Zone Management

1. Navigate to **Government Data Dashboard**
2. Select zone using zone buttons (Canada, USA, Global)
3. View zone-specific data sources and opportunities
4. Apply zone-specific filters
5. Configure zone-specific settings

## Best Practices

- Use appropriate zone for your focus area
- Configure zone-specific filters
- Monitor zone-specific data sources
- Use zone switching for multi-zone analysis

## Related Articles

- [Government Data Dashboard](#rfp-government-data-dashboard)
- [Data Source Integrations](#rfp-data-source-integrations)
- [Geographic Filtering](#rfp-geographic-filtering)
    `,
  },
  {
    id: 'rfp-staff-matching-dashboard',
    title: 'Staff Matching Dashboard',
    category: 'plugins' as HelpCategory,
    summary: 'Match staff members against RFP requirements using interpretable formulas and rule-based matching',
    keywords: ['staff', 'matching', 'requirements', 'rules', 'scores', 'dashboard'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Staff Matching Dashboard

The Staff Matching Dashboard provides a comprehensive interface for matching staff members against RFP requirements using interpretable formulas, rule-based matching, and detailed scoring.

## Overview

The dashboard enables you to:
- View staff matching results with scores and explanations
- Manage matching rules (add/edit/delete/enable/disable)
- Show calculation steps and traceability
- Test matching against sample requirements

## Key Features

### Match Results Display
- Status indicators (matched/partial_match/no_match)
- Match scores (0-100%)
- Calculation steps and traceability
- Result filtering and search

### Matching Rules Management
- Rule creation and editing
- Rule categories and priorities
- Weight configuration
- Rule enabling/disabling

### Test Requirement Interface
- Test requirement input
- Requirement field configuration
- Test matching execution
- Result interpretation

### Matching Statistics
- Total staff count
- Matched/partial/no-match counts
- Average score
- Top score

## Using Staff Matching Dashboard

1. Navigate to **Staff Matching Dashboard**
2. Enter test requirement or select from RFP
3. Click "Test Matching" to execute
4. Review match results and scores
5. Expand results to see calculation steps
6. Manage matching rules as needed

## Best Practices

- Review top matches carefully
- Check calculation steps for transparency
- Adjust rules based on results
- Consider designation requirements
- Verify location and availability

## Related Articles

- [Staff Matching Results](#rfp-staff-matching-results)
- [Matching Rules Management](#rfp-matching-rules-management)
- [Score Calculation](#rfp-score-calculation)
    `,
  },
  {
    id: 'rfp-staff-matching-results',
    title: 'Staff Matching Results',
    category: 'plugins' as HelpCategory,
    summary: 'View and interpret staff matching results with status indicators, scores, and calculation steps',
    keywords: ['staff', 'matching', 'results', 'scores', 'status', 'calculation'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Staff Matching Results

The Staff Matching Results display provides detailed information about how staff members match against RFP requirements, including status indicators, match scores, and calculation steps.

## Result Display

### Status Indicators
- **Matched**: Staff member fully meets requirements (green)
- **Partial Match**: Staff member partially meets requirements (yellow)
- **No Match**: Staff member does not meet requirements (red)

### Match Scores
- Score range: 0-100%
- Color-coded by score range
- Top scores highlighted
- Average score displayed

### Staff Information
- Staff member name and role
- Department and location
- Designations and qualifications
- Experience and expertise

## Matched Rules

Each result shows:
- Rules that were evaluated
- Whether each rule passed or failed
- Score contribution from each rule
- Rule weight and priority
- Rule explanation and message

## Calculation Steps

Expanded view shows:
- Step-by-step calculation process
- Rule evaluation order
- Score aggregation logic
- Final score calculation
- Traceability and transparency

## Filtering and Search

### Status Filter
- Filter by match status
- Show only matched staff
- Show partial matches
- Show no matches

### Search
- Search by staff name
- Search by role or department
- Search by designation
- Real-time filtering

## Using Match Results

1. Execute matching test or load from RFP
2. Review match results list
3. Filter by status or search by name
4. Expand results to see calculation steps
5. Review matched rules and explanations
6. Select staff members for proposal team

## Best Practices

- Review top matches first
- Check calculation steps for transparency
- Consider all matched rules
- Verify designation requirements
- Check location and availability

## Related Articles

- [Staff Matching Dashboard](#rfp-staff-matching-dashboard)
- [Matching Rules Management](#rfp-matching-rules-management)
- [Score Calculation](#rfp-score-calculation)
    `,
  },
  {
    id: 'rfp-matching-rules-management',
    title: 'Matching Rules Management',
    category: 'plugins' as HelpCategory,
    summary: 'Create, edit, and manage staff matching rules with categories, priorities, and weights',
    keywords: ['rules', 'matching', 'management', 'categories', 'priorities', 'weights'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Matching Rules Management

The Matching Rules Management system allows you to create, edit, delete, enable, and disable staff matching rules with configurable categories, priorities, and weights.

## Rule Components

### Rule Categories
- **Designation**: Professional designations (P.Eng, CSAP, QPESA, etc.)
- **Experience**: Years of experience requirements
- **Location**: Geographic location requirements
- **Expertise**: Technical expertise areas
- **Availability**: Staff availability requirements
- **Project History**: Past project experience
- **Custom**: Custom rule expressions

### Rule Priorities
- **Required**: Must pass for match (red)
- **Preferred**: Strongly preferred (yellow)
- **Optional**: Nice to have (gray)

### Rule Weights
- Weight percentage (0-100%)
- Contribution to final score
- Configurable per rule
- Total weights should sum appropriately

## Rule Conditions

### Condition Types
- **Designation Match**: Check for specific designations
- **Experience Range**: Minimum/maximum experience
- **Location Match**: Geographic location matching
- **Expertise Match**: Technical expertise matching
- **Availability Check**: Staff availability verification
- **Project History Match**: Past project experience
- **Custom Expression**: JavaScript expression evaluation

### Condition Configuration
- Field selection
- Operator selection (equals, contains, gte, lte, etc.)
- Value specification
- Custom expression for complex logic

## Rule Management

### Creating Rules
1. Click "Add Rule" button
2. Configure rule name and description
3. Select category and priority
4. Set weight percentage
5. Configure condition
6. Set message and explanation templates
7. Enable rule

### Editing Rules
1. Click edit icon on rule
2. Modify rule configuration
3. Update condition logic
4. Adjust weight if needed
5. Save changes

### Enabling/Disabling Rules
- Toggle rule enabled status
- Disabled rules are not evaluated
- Useful for testing rule changes

### Deleting Rules
- Remove rules that are no longer needed
- Confirm deletion to prevent accidents
- Rules cannot be recovered after deletion

## Using Rules Management

1. Navigate to **Staff Matching Dashboard**
2. Click "Show Rules" to view rules panel
3. Click "Add Rule" to create new rule
4. Configure rule settings
5. Save rule
6. Test rule with matching test

## Best Practices

- Use appropriate rule categories
- Set realistic weights
- Test rules before enabling
- Document rule purpose in description
- Review rule performance regularly

## Related Articles

- [Staff Matching Dashboard](#rfp-staff-matching-dashboard)
- [Rule Conditions](#rfp-rule-conditions)
- [Score Calculation](#rfp-score-calculation)
    `,
  },
  {
    id: 'rfp-rule-conditions',
    title: 'Rule Conditions',
    category: 'plugins' as HelpCategory,
    summary: 'Configure rule conditions with field operators, value matching, and JavaScript expressions',
    keywords: ['rules', 'conditions', 'operators', 'expressions', 'matching'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Rule Conditions

Rule conditions define the logic for evaluating whether a staff member matches a requirement. Conditions can use field operators, value matching, or JavaScript expressions.

## Condition Types

### Designation Match
- Check if staff has specific designations
- Supports multiple designations
- Case-insensitive matching
- Example: Check for P.Eng or CSAP

### Experience Range
- Minimum years of experience
- Maximum years of experience
- Range-based matching
- Example: 5-15 years experience

### Location Match
- Province/state matching
- City matching
- Office location matching
- Example: BC or Ontario location

### Expertise Match
- Technical expertise areas
- Service line matching
- Industry experience
- Example: Contaminated Sites expertise

### Availability Check
- Staff availability status
- Capacity verification
- Project commitment check
- Example: Available for new projects

### Project History Match
- Past project experience
- Client experience
- Project type experience
- Example: Similar project experience

### Custom Expression
- JavaScript expression evaluation
- Full access to staff data
- Complex logic support
- Example: Custom scoring formula

## Field Operators

### Equals
- Exact value match
- Case-sensitive
- Use for specific values

### Contains
- Substring matching
- Case-insensitive
- Use for partial matches

### Greater Than or Equal (gte)
- Numeric comparison
- Use for minimum values
- Example: Experience >= 5 years

### Less Than or Equal (lte)
- Numeric comparison
- Use for maximum values
- Example: Experience <= 20 years

### In
- Value in array
- Multiple value matching
- Example: Designation in [P.Eng, CSAP]

### Pattern Match
- Regular expression matching
- Pattern-based matching
- Example: Email pattern matching

### Any
- Any value matches
- Array-based matching
- Example: Any expertise matches

### All
- All values match
- Array-based matching
- Example: All designations required

## Value Specification

### Simple Values
- String values
- Numeric values
- Boolean values

### Array Values
- Multiple values
- JSON array format
- Use with "in" or "contains" operators

### Object Values
- Complex value structures
- JSON object format
- Use for nested data matching

## Custom Expressions

### JavaScript Syntax
- Full JavaScript expression support
- Access to staff data object
- Return boolean or numeric result

### Expression Examples
\`\`\`
staff.designations.includes('P.Eng')
staff.experience_years >= 5
staff.location.province === 'BC'
\`\`\`

## Using Rule Conditions

1. Select condition type
2. Configure field and operator (if applicable)
3. Specify value or expression
4. Test condition logic
5. Save condition

## Best Practices

- Use appropriate condition type
- Test conditions thoroughly
- Use custom expressions for complex logic
- Document condition purpose
- Keep conditions simple when possible

## Related Articles

- [Matching Rules Management](#rfp-matching-rules-management)
- [Score Calculation](#rfp-score-calculation)
    `,
  },
  {
    id: 'rfp-score-calculation',
    title: 'Score Calculation',
    category: 'plugins' as HelpCategory,
    summary: 'Understand how staff matching scores are calculated with weight-based scoring and rule evaluation',
    keywords: ['score', 'calculation', 'weights', 'rules', 'evaluation', 'aggregation'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Score Calculation

The Staff Matching score calculation system uses weight-based scoring, rule evaluation, and score aggregation to determine how well staff members match RFP requirements.

## Score Components

### Rule Evaluation
- Each rule is evaluated independently
- Rules return pass/fail or numeric score
- Rule results contribute to final score

### Weight-Based Scoring
- Each rule has a weight percentage
- Weight determines contribution to final score
- Total weights should be normalized appropriately

### Score Aggregation
- Rule scores are aggregated
- Weighted sum calculation
- Final score range: 0-100%

## Calculation Process

### Step 1: Rule Evaluation
1. Evaluate each enabled rule
2. Check rule condition
3. Determine rule result (pass/fail/score)
4. Calculate rule score contribution

### Step 2: Weight Application
1. Apply rule weight to score contribution
2. Calculate weighted score
3. Sum weighted scores
4. Normalize to 0-100% range

### Step 3: Final Score
1. Aggregate all rule scores
2. Apply normalization
3. Calculate final match score
4. Determine match status

## Score Contribution

### Rule Score Contribution
- Pass: Full weight contribution
- Fail: Zero contribution
- Partial: Proportional contribution
- Custom: Score calculation result

### Weight Distribution
- Required rules: Higher weights
- Preferred rules: Medium weights
- Optional rules: Lower weights
- Total weights: Should sum appropriately

## Match Status Determination

### Matched (70-100%)
- High score indicates good match
- Most required rules pass
- Strong capability alignment

### Partial Match (30-69%)
- Moderate score indicates partial match
- Some required rules may fail
- Some capability gaps

### No Match (0-29%)
- Low score indicates poor match
- Many required rules fail
- Significant capability gaps

## Calculation Transparency

### Calculation Steps
- Step-by-step calculation display
- Rule evaluation order
- Score contribution breakdown
- Final score calculation

### Traceability
- Full calculation traceability
- Rule-by-rule breakdown
- Weight application details
- Score aggregation logic

## Using Score Calculation

1. Execute matching test
2. Review match results
3. Expand result to see calculation steps
4. Review rule-by-rule breakdown
5. Understand score contribution
6. Adjust rules if needed

## Best Practices

- Review calculation steps for transparency
- Adjust rule weights based on importance
- Ensure required rules have higher weights
- Test rule changes before deploying
- Monitor score distributions

## Related Articles

- [Staff Matching Results](#rfp-staff-matching-results)
- [Matching Rules Management](#rfp-matching-rules-management)
- [Rule Conditions](#rfp-rule-conditions)
    `,
  },
  {
    id: 'rfp-citation-reference-system',
    title: 'Citation & Reference System',
    category: 'plugins' as HelpCategory,
    summary: 'Automatic citation generation with source traceability, document-level and page-level citations',
    keywords: ['citations', 'references', 'traceability', 'sources', 'documents', 'evidence'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Citation & Reference System

All AI-generated content includes comprehensive citations and references to the source documents from the knowledge base, ensuring full traceability and minimizing hallucination.

## Citation Features

### Automatic Citation
- All AI outputs automatically include citations
- Citations reference specific documents and pages
- Maintains consistent citation format
- Supports multiple citation styles

### Source Traceability
- Full traceability from proposal content to source documents
- Document-level and page-level citations
- Evidence chain visualization
- Source document access from citations

### Citation Validation
- Validates citation accuracy and completeness
- Ensures all claims are properly cited
- Checks for missing or broken citations
- Provides citation quality scores

### Reference Management
- Manages bibliography and reference lists
- Tracks citation usage across proposals
- Provides citation analytics
- Supports citation export and formatting

## Citation Format

### Document Citation
- Document title and source
- Document metadata (date, type, etc.)
- Clickable links to source documents
- Visual indicators for citation quality

### Page-Level Citation
- Page numbers for precise attribution
- Page-specific content references
- Accurate source location
- Context preservation

## Citation Integration

### In Generated Content
- Citations embedded in proposal text
- Automatic citation insertion
- Citation format consistency
- Source document links

### Citation Display
- Inline citations in content
- Citation tooltips on hover
- Citation list at end of sections
- Full bibliography at end of proposal

## Using Citations

1. Generate proposal or section content
2. Review automatic citations
3. Click citations to view source documents
4. Verify citation accuracy
5. Add additional citations if needed

## Best Practices

- Verify all citations are accurate
- Check source documents for context
- Ensure citations support all claims
- Use page-level citations for precision
- Maintain citation format consistency

## Related Articles

- [AI-Powered Proposal Generation](#rfp-ai-proposal-generation)
- [AI-Powered Section Editing](#rfp-ai-section-editing)
- [Evidence Basket System](#rfp-evidence-basket-system)
    `,
  },
  {
    id: 'rfp-evidence-basket-system',
    title: 'Evidence Basket System',
    category: 'plugins' as HelpCategory,
    summary: 'Collect and manage evidence from multiple sources for use in proposal generation',
    keywords: ['evidence', 'basket', 'collection', 'sources', 'proposals', 'documents'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Evidence Basket System

The Evidence Basket system allows you to collect and manage evidence from multiple sources (proposals, documents, search results) for use in proposal generation.

## Features

### Evidence Collection
- Add evidence from search results
- Add evidence from proposal search
- Add evidence from document viewing
- Collect evidence from multiple sources

### Basket Management
- View all collected evidence
- Remove evidence from basket
- Organize evidence by category
- Clear basket when done

### Evidence Usage
- Use evidence in proposal generation
- Reference evidence in sections
- Include evidence in citations
- Export evidence for external use

## Using Evidence Basket

### Adding Evidence
1. Search for proposals or documents
2. Select relevant results
3. Click "Add to Basket" button
4. Evidence added to basket

### Managing Basket
1. View evidence basket
2. Review collected evidence
3. Remove unwanted evidence
4. Organize evidence as needed

### Using Evidence
1. Start proposal generation
2. Evidence automatically considered
3. Citations include basket evidence
4. Evidence referenced in content

## Best Practices

- Collect relevant evidence before generating
- Review evidence for quality and relevance
- Organize evidence by section or topic
- Clear basket after proposal completion
- Use evidence to support all claims

## Related Articles

- [Proposal Search Tab](#rfp-proposal-search-tab)
- [AI-Powered Proposal Generation](#rfp-ai-proposal-generation)
- [Citation & Reference System](#rfp-citation-reference-system)
    `,
  },
  {
    id: 'rfp-review-checklist-system',
    title: 'Review Checklist System',
    category: 'plugins' as HelpCategory,
    summary: 'RFP-specific review checklist with items, notes, and completion tracking',
    keywords: ['review', 'checklist', 'quality', 'compliance', 'approval'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Review Checklist System

The Review Checklist system provides RFP-specific review checklists with items, notes management, and completion tracking for quality assurance.

## Checklist Features

### RFP-Specific Items
- Compliance with RFP requirements
- Technical accuracy and completeness
- Citation coverage and accuracy
- Quality and professionalism
- Client-specific requirements

### Checklist Management
- Check/uncheck items as reviewed
- Add notes to checklist items
- Track completion status
- Require all items for approval

### Notes System
- Add notes to specific items
- General feedback notes
- Revision requests
- Approval notes

## Using Review Checklist

1. Navigate to proposal in Review Workflow
2. Review checklist items
3. Check items as completed
4. Add notes for items requiring attention
5. Complete all required items
6. Submit for approval

## Best Practices

- Complete checklist thoroughly
- Add specific, actionable notes
- Address all checklist items
- Use notes for communication
- Track completion progress

## Related Articles

- [Review Workflow Tab](#rfp-review-workflow-tab)
- [Proposal Management Dashboard](#rfp-proposal-management-dashboard)
    `,
  },
  {
    id: 'rfp-proposal-builder-component',
    title: 'Proposal Builder Component',
    category: 'plugins' as HelpCategory,
    summary: 'Modal interface for building proposals with opportunity selection, client information, and generation options',
    keywords: ['proposal', 'builder', 'generation', 'opportunity', 'client', 'modal'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Proposal Builder Component

The Proposal Builder component provides a modal interface for building proposals with opportunity selection, client information configuration, and proposal generation options.

## Features

### Opportunity Selection
- Select opportunity from dashboard
- View opportunity details
- Verify opportunity information
- Confirm opportunity selection

### Client Information
- Client name configuration
- Organization details
- Contact information
- Client-specific settings

### Proposal Generation Options
- Template selection
- Section customization
- Evidence source selection
- Generation preferences

### Progress Tracking
- Real-time generation progress
- Section-by-section completion
- Estimated time remaining
- Generation status updates

## Using Proposal Builder

1. Navigate to opportunity in dashboard
2. Click "Generate Proposal" button
3. Proposal Builder modal opens
4. Review opportunity and client information
5. Configure generation options
6. Click "Start Generation"
7. Monitor progress
8. Review generated proposal

## Generation Process

### Step 1: Opportunity Analysis
- System analyzes opportunity document
- Extracts requirements
- Identifies relevant capabilities

### Step 2: Template Generation
- Generates proposal template
- Creates section structure
- Maps questions to evidence

### Step 3: Content Generation
- Generates proposal sections
- Retrieves relevant evidence
- Creates citations
- Validates content

### Step 4: Quality Assurance
- Validates against requirements
- Checks citation coverage
- Ensures completeness
- Provides quality scores

## Completion Handling

### On Success
- Proposal saved to system
- Navigate to proposal view
- Show success notification
- Enable editing

### On Cancel
- Close modal
- Discard progress
- Return to dashboard

## Best Practices

- Review opportunity details before generating
- Configure client information accurately
- Select appropriate generation options
- Monitor generation progress
- Review generated content carefully

## Related Articles

- [AI-Powered Proposal Generation](#rfp-ai-proposal-generation)
- [Proposal Management Dashboard](#rfp-proposal-management-dashboard)
- [Proposals List Tab](#rfp-proposal-list-tab)
    `,
  },
  {
    id: 'rfp-sync-management',
    title: 'Sync Management',
    category: 'plugins' as HelpCategory,
    summary: 'Manage manual and scheduled sync operations for data sources with status monitoring and error handling',
    keywords: ['sync', 'management', 'scheduled', 'manual', 'status', 'monitoring'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Sync Management

Sync Management provides control over manual and scheduled sync operations for data sources, with status monitoring, sync history, and error handling.

## Sync Types

### Manual Sync
- Trigger sync on demand
- Immediate execution
- Full or incremental sync
- Sync status feedback

### Scheduled Sync
- Automatic sync at intervals
- Configurable frequency
- Time-based scheduling
- Background execution

## Sync Configuration

### Sync Frequency
- **Real-time**: Immediate sync on changes
- **Hourly**: Sync every hour
- **Daily**: Sync once per day
- **Weekly**: Sync once per week
- **Custom**: User-defined interval

### Sync Scope
- **Full Sync**: Sync all opportunities
- **Incremental Sync**: Sync only new/changed opportunities
- **Filtered Sync**: Sync based on filters (geographic, service line, keywords)

## Sync Status Monitoring

### Status Indicators
- **Success**: Sync completed successfully
- **In Progress**: Sync currently running
- **Failed**: Sync encountered errors
- **Pending**: Sync scheduled but not started

### Status Details
- Last sync timestamp
- Next scheduled sync
- Opportunities synced count
- Error messages (if any)

## Sync History

### History View
- View past sync operations
- See sync results
- Review error logs
- Track sync performance

### History Information
- Sync timestamp
- Sync duration
- Opportunities synced
- Success/failure status
- Error details

## Error Handling

### Error Detection
- Automatic error detection
- Error categorization
- Error notification
- Error logging

### Error Recovery
- Automatic retry on failure
- Manual retry option
- Error resolution guidance
- Support contact information

## Using Sync Management

1. Navigate to **Data Integrations** tab
2. Select data source
3. Click "Sync Now" for manual sync
4. Configure sync schedule if needed
5. Monitor sync status
6. Review sync history
7. Address errors if any

## Best Practices

- Use scheduled sync for regular updates
- Monitor sync status regularly
- Review sync history for issues
- Configure appropriate sync frequency
- Use filtered sync to reduce load
- Address sync errors promptly

## Related Articles

- [Data Integrations Tab](#rfp-integrations-tab)
- [Data Source Integrations](#rfp-data-source-integrations)
    `,
  },
  {
    id: 'rfp-requirements-tab',
    title: 'Requirements Tab (Coming Soon)',
    category: 'plugins' as HelpCategory,
    summary: 'Automated requirement extraction from RFPs with analysis, compliance tracking, and requirement-to-capability mapping',
    keywords: ['requirements', 'extraction', 'rfp', 'compliance', 'mapping', 'analysis'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    comingSoon: true,
    content: `
# Requirements Tab (Coming Soon)

The Requirements tab will provide automated requirement extraction from RFPs with comprehensive analysis, compliance tracking, and requirement-to-capability mapping.

## Planned Features

### Requirement Extraction
- Automatic extraction from RFP documents
- Requirement identification and categorization
- Mandatory vs. optional requirement classification
- Requirement numbering and organization

### Requirement Analysis
- Requirement complexity assessment
- Requirement dependency mapping
- Requirement grouping and organization
- Requirement priority identification

### Compliance Tracking
- Track requirement compliance status
- Identify compliance gaps
- Generate compliance matrices
- Monitor compliance progress

### Requirement-to-Capability Mapping
- Map requirements to company capabilities
- Identify capability gaps
- Suggest capability enhancements
- Track capability coverage

## Expected Workflow

1. Upload or select RFP document
2. System extracts requirements automatically
3. Review extracted requirements
4. Analyze requirement complexity and dependencies
5. Map requirements to capabilities
6. Track compliance status
7. Generate compliance matrix

## Use Cases

- **Proposal Preparation**: Understand all requirements before writing
- **Compliance Assurance**: Ensure all requirements are addressed
- **Gap Analysis**: Identify missing capabilities
- **Team Assignment**: Assign requirements to team members

## Related Articles

- [Proposal Management Dashboard](#rfp-proposal-management-dashboard)
- [AI-Powered Template Generation](#rfp-ai-template-generation)
    `,
  },
  {
    id: 'rfp-opportunity-matching-tab',
    title: 'Opportunity Matching Tab (Coming Soon)',
    category: 'plugins' as HelpCategory,
    summary: 'AI-powered opportunity-to-capability matching with match scores, feasibility assessment, and prioritization',
    keywords: ['opportunity', 'matching', 'capabilities', 'scores', 'feasibility', 'prioritization'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    comingSoon: true,
    content: `
# Opportunity Matching Tab (Coming Soon)

The Opportunity Matching tab will provide AI-powered opportunity-to-capability matching with match score calculation, feasibility assessment, and opportunity prioritization.

## Planned Features

### Opportunity-to-Capability Matching
- Automatic matching of opportunities to company capabilities
- Match score calculation (0-100%)
- Capability gap identification
- Strength and weakness analysis

### Match Score Calculation
- Weighted scoring based on requirements
- Capability alignment scoring
- Past performance consideration
- Geographic and resource availability factors

### Feasibility Assessment
- Overall feasibility score
- Requirement-by-requirement analysis
- Risk factor identification
- Go/no-go recommendations

### Opportunity Prioritization
- Rank opportunities by match score
- Prioritize based on feasibility
- Consider strategic factors
- Recommend pursuit opportunities

## Expected Workflow

1. View opportunities in dashboard
2. System calculates match scores automatically
3. Review match scores and feasibility assessments
4. Analyze capability alignment
5. Prioritize opportunities
6. Make go/no-go decisions

## Use Cases

- **Opportunity Evaluation**: Quickly assess opportunity fit
- **Resource Planning**: Identify resource requirements
- **Strategic Planning**: Prioritize opportunities strategically
- **Bid/No-Bid Decisions**: Make informed decisions

## Related Articles

- [Proposal Management Dashboard](#rfp-proposal-management-dashboard)
- [AI-Powered Feasibility Assessment](#rfp-ai-feasibility-assessment)
- [Pipeline Tab](#rfp-pipeline-tab)
    `,
  },
  {
    id: 'rfp-awards-tab',
    title: 'Awards Tab (Coming Soon)',
    category: 'plugins' as HelpCategory,
    summary: 'View contract awards and spending data with competitive intelligence, award patterns, and trend tracking',
    keywords: ['awards', 'contracts', 'spending', 'competitive', 'intelligence', 'trends'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    comingSoon: true,
    content: `
# Awards Tab (Coming Soon)

The Awards tab will provide comprehensive viewing of contract awards and spending data with competitive intelligence, award pattern analysis, and trend tracking.

## Planned Features

### Contract Awards Viewing
- View all contract awards
- Filter by agency, value, date
- Search award information
- Export award data

### Competitive Intelligence
- Identify awarded contractors
- Analyze competitor activity
- Track competitor wins
- Monitor market share

### Award Patterns
- Identify award patterns
- Analyze award trends
- Track award frequencies
- Monitor award values

### Trend Tracking
- Track award trends over time
- Analyze seasonal patterns
- Monitor market changes
- Identify opportunities

## Expected Workflow

1. Navigate to Awards tab
2. View contract awards
3. Filter and search awards
4. Analyze competitive intelligence
5. Track award patterns
6. Monitor trends

## Use Cases

- **Competitive Analysis**: Understand competitor activity
- **Market Intelligence**: Track market trends
- **Opportunity Identification**: Identify potential opportunities
- **Strategic Planning**: Inform strategic decisions

## Related Articles

- [Government Data Dashboard](#rfp-government-data-dashboard)
- [Opportunities Tab](#rfp-opportunities-tab)
    `,
  },
  {
    id: 'rfp-rss-feed-management',
    title: 'RSS Feed Management',
    category: 'plugins' as HelpCategory,
    summary: 'Manage RSS feeds for industry news with feed selection, priority levels, keyword filtering, and auto-refresh',
    keywords: ['rss', 'feeds', 'news', 'industry', 'filtering', 'refresh'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# RSS Feed Management

RSS Feed Management provides comprehensive control over RSS feeds for industry news monitoring, including feed selection, priority configuration, keyword filtering, and auto-refresh settings.

## Feed Registry

### Feed Categories
- **Industry News**: General industry updates
- **Government News**: Government procurement news
- **Regulatory News**: Regulatory and policy updates
- **Technology News**: Technology and innovation news

### Priority Levels
- **Priority 1**: High-priority feeds (always active)
- **Priority 2**: Medium-priority feeds (selectable)
- **Priority 3**: Low-priority feeds (optional)

## Feed Selection

### Selecting Feeds
- Choose active feeds from registry
- Enable/disable feeds as needed
- Configure feed priorities
- Save feed selection

### Feed Configuration
- Feed URL configuration
- Feed name and description
- Feed category assignment
- Feed priority setting

## Keyword Filtering

### Keyword Configuration
- Set keywords for feed filtering
- Zone-specific keyword filtering
- Keyword-based relevance ranking
- Saved keyword sets

### Filtering Behavior
- Filter items by keywords
- Highlight matching keywords
- Rank by keyword relevance
- Exclude irrelevant items

## Auto-Refresh

### Refresh Configuration
- Enable/disable auto-refresh
- Set refresh interval
- Configure refresh timing
- Manual refresh option

### Refresh Status
- Last refresh timestamp
- Next refresh time
- Refresh success/failure
- Refresh error handling

## Feed Health Monitoring

### Health Checks
- Feed availability checking
- Feed response time monitoring
- Feed error detection
- Feed health status

### Health Status
- **Healthy**: Feed responding normally
- **Degraded**: Feed slow or intermittent
- **Unhealthy**: Feed not responding
- **Unknown**: Health status unclear

## Using RSS Feed Management

1. Navigate to **Industry News** tab
2. Click feed selector or settings
3. Select active feeds
4. Configure keywords
5. Set auto-refresh interval
6. Monitor feed health
7. Review news items

## Best Practices

- Select relevant feeds for your needs
- Use keywords to filter relevant news
- Monitor feed health regularly
- Adjust refresh intervals based on needs
- Review and update feed selection periodically

## Related Articles

- [Industry News Tab](#rfp-news-tab)
- [Government Data Dashboard](#rfp-government-data-dashboard)
    `,
  },
  {
    id: 'rfp-us-government-integrations',
    title: 'US Government Integrations',
    category: 'plugins' as HelpCategory,
    summary: 'Integrate with US government data sources including SAM.gov, Federal Register, Executive Orders, and Grants.gov',
    keywords: ['usa', 'government', 'sam-gov', 'federal-register', 'executive-orders', 'grants'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# US Government Integrations

US Government Integrations provide access to comprehensive US federal procurement data, regulatory information, and grant opportunities through multiple data sources.

## Data Sources

### SAM.gov
- **Purpose**: Federal contracting opportunities
- **Coverage**: All US federal agencies
- **Opportunity Types**: RFPs, RFQs, RFIs, Sources Sought
- **Authentication**: API key required
- **Sync Frequency**: Daily recommended

### Federal Register
- **Purpose**: Regulatory notices and opportunities
- **Coverage**: Federal agency notices
- **Document Types**: Proposed rules, notices, opportunities
- **Authentication**: Public API (no auth required)
- **Sync Frequency**: Hourly recommended

### Executive Orders
- **Purpose**: Presidential executive orders
- **Coverage**: All executive orders
- **Document Types**: Executive orders, memoranda
- **Authentication**: Public API (no auth required)
- **Sync Frequency**: Daily recommended

### Grants.gov
- **Purpose**: Federal grant opportunities
- **Coverage**: All federal grant programs
- **Opportunity Types**: Grants, cooperative agreements
- **Authentication**: API key required
- **Sync Frequency**: Daily recommended

### USA Spending
- **Purpose**: Contract awards and spending data
- **Coverage**: Federal contract awards
- **Data Types**: Award data, spending analytics
- **Authentication**: Public API (no auth required)
- **Sync Frequency**: Weekly recommended

## Configuration

### API Keys
- SAM.gov API key configuration
- Grants.gov API key configuration
- API key storage in environment variables
- API key rotation procedures

### Filters
- Keyword filtering
- Agency filtering
- Opportunity type filtering
- Date range filtering

## Using US Government Integrations

1. Navigate to **Government Data Dashboard**
2. Select **USA** zone
3. Go to **Data Integrations** tab
4. Configure US data sources
5. Set up API keys
6. Configure filters
7. Enable sync

## Best Practices

- Obtain API keys from official sources
- Configure appropriate sync frequencies
- Use keywords to filter relevant opportunities
- Monitor sync status regularly
- Review opportunities in Opportunities tab

## Related Articles

- [Government Data Dashboard](#rfp-government-data-dashboard)
- [Data Integrations Tab](#rfp-integrations-tab)
- [Zone Management System](#rfp-zone-management)
    `,
  },
  {
    id: 'rfp-international-data-sources',
    title: 'International Data Sources',
    category: 'plugins' as HelpCategory,
    summary: 'Integrate with international data sources including World Bank for global opportunity discovery',
    keywords: ['international', 'global', 'world-bank', 'opportunities', 'development'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# International Data Sources

International Data Sources provide access to global procurement opportunities and development projects through international organizations.

## Data Sources

### World Bank
- **Purpose**: Global infrastructure and development projects
- **Coverage**: Worldwide development projects
- **Project Types**: Infrastructure, development, consulting
- **Authentication**: Public API (no auth required)
- **Sync Frequency**: Weekly recommended

### Global Filtering
- Filter by region
- Filter by country
- Filter by project type
- Filter by value range

## Configuration

### World Bank Integration
- Enable World Bank data source
- Configure sync frequency
- Set up geographic filters
- Configure keyword filters

### Multi-Zone Data Aggregation
- Aggregate data from multiple zones
- Combine Canada, USA, and Global data
- Unified opportunity view
- Cross-zone filtering

## Using International Data Sources

1. Navigate to **Government Data Dashboard**
2. Select **Global** zone
3. Go to **Data Integrations** tab
4. Enable World Bank integration
5. Configure filters
6. Enable sync
7. Review opportunities

## Best Practices

- Use geographic filters for relevant regions
- Monitor international opportunities regularly
- Consider local requirements and regulations
- Evaluate feasibility for international work

## Related Articles

- [Government Data Dashboard](#rfp-government-data-dashboard)
- [Zone Management System](#rfp-zone-management)
- [Data Source Integrations](#rfp-data-source-integrations)
    `,
  },
  {
    id: 'rfp-staff-data-integration',
    title: 'Staff Data Integration',
    category: 'plugins' as HelpCategory,
    summary: 'View and manage staff data with entity integration, department filtering, and designation filtering',
    keywords: ['staff', 'data', 'entities', 'department', 'designation', 'filtering'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Staff Data Integration

Staff Data Integration provides comprehensive viewing and management of staff data with entity-based storage, filtering capabilities, and profile access.

## Staff Entity Structure

### Entity Type
- **Entity Type**: staff_member
- **Storage**: Entity-based storage in TraceOS
- **Relationships**: Linked to projects, qualifications, certifications

### Staff Data Fields
- Name and role
- Department and location
- Designations and qualifications
- Experience and expertise
- Availability and capacity

## Staff Data Viewing

### Staff List View
- View all staff members
- Search by name or role
- Filter by department
- Filter by designation

### Staff Profile
- Detailed staff information
- Qualifications and certifications
- Past project experience
- Expertise areas
- Availability status

## Filtering Options

### Department Filter
- Filter by department
- Multiple department selection
- Department-based organization

### Designation Filter
- Filter by professional designation
- Multiple designation selection
- Designation-based filtering

### Location Filter
- Filter by office location
- Filter by province/state
- Filter by city

## Staff Data Management

### Data Import
- Import staff data from CSV
- Bulk staff data updates
- Data validation
- Error handling

### Data Updates
- Update staff information
- Add qualifications
- Update availability
- Modify designations

## Using Staff Data Integration

1. Navigate to **Staff Management** page
2. View staff data in **Staff Data** tab
3. Use filters to find staff
4. Click staff member to view profile
5. Update staff information as needed

## Best Practices

- Keep staff data up to date
- Regularly update availability
- Maintain accurate qualifications
- Use filters for efficient searching

## Related Articles

- [Staff Matching Dashboard](#rfp-staff-matching-dashboard)
- [Qualification Management](#rfp-qualification-management)
    `,
  },
  {
    id: 'rfp-qualification-management',
    title: 'Qualification Management',
    category: 'plugins' as HelpCategory,
    summary: 'View and manage staff qualifications with certification stats, deduplication, and requirement mapping',
    keywords: ['qualifications', 'certifications', 'deduplication', 'stats', 'mapping'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Qualification Management

Qualification Management provides comprehensive viewing and management of staff qualifications, certifications, and their mapping to RFP requirements.

## Qualification Viewing

### Qualification List
- View all qualifications
- Search qualifications
- Filter by type
- View qualification details

### Certification Stats
- Total certifications count
- Certifications by type
- Certifications by staff member
- Certification expiration tracking

## Qualification Deduplication

### Deduplication Process
- Identify duplicate qualifications
- Merge duplicate entries
- Maintain data integrity
- Preserve relationships

### Deduplication Features
- Automatic duplicate detection
- Manual deduplication option
- Deduplication reports
- Data validation

## Qualification-to-Requirement Mapping

### Requirement Mapping
- Map qualifications to RFP requirements
- Identify qualification gaps
- Track qualification coverage
- Generate mapping reports

### Mapping Features
- Automatic mapping suggestions
- Manual mapping configuration
- Mapping validation
- Coverage analysis

## Using Qualification Management

1. Navigate to **Staff Management** page
2. Go to **Qualifications** tab
3. View qualification list and stats
4. Run deduplication if needed
5. Review qualification-to-requirement mappings

## Best Practices

- Regularly review qualifications
- Run deduplication periodically
- Maintain accurate qualification data
- Update qualification-to-requirement mappings
- Track certification expirations

## Related Articles

- [Staff Data Integration](#rfp-staff-data-integration)
- [Staff Matching Dashboard](#rfp-staff-matching-dashboard)
    `,
  },
  {
    id: 'rfp-staff-matching-best-practices',
    title: 'Staff Matching Best Practices',
    category: 'plugins' as HelpCategory,
    summary: 'Effective strategies for staff matching including rule optimization, weight tuning, and performance optimization',
    keywords: ['staff', 'matching', 'best-practices', 'optimization', 'rules', 'weights'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Staff Matching Best Practices

Effective strategies for optimizing staff matching results through rule configuration, weight tuning, threshold adjustment, and performance optimization.

## Rule Optimization

### Rule Design
- Create specific, focused rules
- Avoid overly complex rules
- Test rules before deploying
- Document rule purpose

### Rule Categories
- Use appropriate rule categories
- Group related rules
- Prioritize required rules
- Balance rule coverage

## Weight Tuning

### Weight Distribution
- Assign higher weights to required rules
- Balance weights across rule categories
- Ensure total weights are appropriate
- Test weight changes

### Weight Adjustment
- Start with default weights
- Adjust based on results
- Test weight changes
- Monitor score distributions

## Threshold Adjustment

### Match Status Thresholds
- **Matched**: 70-100% (adjustable)
- **Partial Match**: 30-69% (adjustable)
- **No Match**: 0-29% (adjustable)

### Threshold Configuration
- Adjust thresholds based on needs
- Consider designation requirements
- Account for experience levels
- Test threshold changes

## Performance Optimization

### Rule Performance
- Monitor rule evaluation time
- Optimize slow rules
- Disable unnecessary rules
- Cache rule results when possible

### Matching Performance
- Limit number of rules
- Optimize rule conditions
- Use efficient operators
- Monitor matching time

## Effective Matching Strategies

### Requirement Analysis
- Understand requirement priorities
- Identify mandatory vs. preferred
- Consider requirement dependencies
- Analyze requirement complexity

### Staff Evaluation
- Review top matches carefully
- Consider all matched rules
- Verify designation requirements
- Check location and availability

### Decision Making
- Use match scores as guidance
- Consider qualitative factors
- Review calculation steps
- Make informed decisions

## Best Practices Summary

1. **Design Clear Rules**: Create specific, focused rules
2. **Balance Weights**: Distribute weights appropriately
3. **Test Changes**: Test rule and weight changes
4. **Monitor Performance**: Track matching performance
5. **Review Results**: Regularly review match results
6. **Optimize Rules**: Optimize slow or ineffective rules
7. **Document Changes**: Document rule and weight changes

## Related Articles

- [Staff Matching Dashboard](#rfp-staff-matching-dashboard)
- [Matching Rules Management](#rfp-matching-rules-management)
- [Score Calculation](#rfp-score-calculation)
    `,
  },
  {
    id: 'rfp-proposal-generation-workflow',
    title: 'Proposal Generation Workflow',
    category: 'workflows' as HelpCategory,
    summary: 'Complete end-to-end workflow for proposal creation from opportunity selection to final submission',
    keywords: ['workflow', 'proposal', 'generation', 'creation', 'process', 'steps'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Proposal Generation Workflow

Complete end-to-end workflow for proposal creation from opportunity discovery through template generation, AI-powered content creation, section editing, and final submission.

## Workflow Overview

### Step 1: Opportunity Discovery
- Browse opportunities in dashboard
- Use filters and search
- Review opportunity summaries
- Assess feasibility

### Step 2: Opportunity Selection
- Select opportunity for proposal
- Review opportunity details
- Verify requirements
- Confirm selection

### Step 3: Template Generation
- Generate proposal template
- Review template structure
- Customize sections as needed
- Verify requirement coverage

### Step 4: Content Generation
- Generate proposal sections
- Review generated content
- Verify citations
- Check quality scores

### Step 5: Content Refinement
- Edit and refine sections
- Use AI rewriting features
- Enhance technical content
- Improve clarity and tone

### Step 6: Review and Approval
- Submit for senior review
- Complete review checklist
- Address reviewer feedback
- Obtain approval

### Step 7: Final Preparation
- Final quality check
- Verify all requirements
- Check citation accuracy
- Prepare for submission

### Step 8: Submission
- Export proposal
- Submit to client
- Track submission status
- Update opportunity status

## Workflow Features

### AI Integration
- AI-powered at every step
- Keyword suggestions
- Opportunity summaries
- Feasibility assessment
- Template generation
- Content generation
- Section editing

### Citation System
- Automatic citations
- Source traceability
- Citation validation
- Reference management

### Quality Assurance
- Quality scores
- Compliance checking
- Requirement validation
- Citation coverage

## Using the Workflow

1. Start from opportunity in dashboard
2. Follow workflow steps sequentially
3. Use AI features at each step
4. Review and refine content
5. Complete review process
6. Submit proposal

## Best Practices

- Start early to allow time for review
- Use AI features to accelerate creation
- Review all generated content carefully
- Verify citations and sources
- Complete review checklist thoroughly
- Allow time for revisions

## Related Articles

- [Proposal Management Dashboard](#rfp-proposal-management-dashboard)
- [AI-Powered Proposal Generation](#rfp-ai-proposal-generation)
- [Review Workflow Tab](#rfp-review-workflow-tab)
    `,
  },
  {
    id: 'rfp-data-source-integrations-detailed',
    title: 'Data Source Integrations - Detailed Guide',
    category: 'sources' as HelpCategory,
    summary: 'Comprehensive guide to all 15+ data source integrations with detailed configuration, authentication, and troubleshooting',
    keywords: ['data-sources', 'integrations', 'configuration', 'authentication', 'bc-bid', 'merx', 'sap-ariba'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Data Source Integrations - Detailed Guide

Comprehensive guide to configuring and managing all 15+ data source integrations for opportunity discovery.

## Canadian Data Sources

### BC Bid
- **URL**: http://www.bcbid.gov.bc.ca/
- **Authentication**: Username/password (stored in Keepass)
- **Coverage**: British Columbia government opportunities
- **Sync Frequency**: Daily recommended
- **Credentials**: bdonald / Dominion2021
- **Features**: Local staff requirements, competitive bidding

### Bids&Tender
- **URL**: https://www.bidsandtenders.com
- **Authentication**: Username/password
- **Coverage**: BC and Ontario opportunities
- **Sync Frequency**: Daily recommended
- **Credentials**: rrai@keystoneenvironmental.ca
- **Features**: Budget constraints, capacity limitations

### Bonfire
- **URL**: bonefirehub.ca
- **Authentication**: Multiple accounts
- **Coverage**: BC cities (Vancouver, Victoria, etc.)
- **Sync Frequency**: Daily recommended
- **Credentials**: rrai@keystoneenvironmental.ca, aboons@keystoneenvironmental.ca
- **Features**: Healthcare projects, multi-phase projects

### Merx
- **URL**: https://www.merx.com
- **Authentication**: Multiple accounts
- **Coverage**: Ontario-focused, some federal
- **Sync Frequency**: Daily recommended
- **Credentials**: wdonald, keystoneenviro, jmuir@keystoneenvironmental.ca
- **Features**: First Nations work, remote locations

### SAP Ariba
- **URL**: Varies by account
- **Authentication**: Multiple accounts
- **Coverage**: Federal and provincial
- **Sync Frequency**: Daily recommended
- **Credentials**: rrai@keystoneenvironmental.ca, rgrewal@keystoneenvironmental.ca
- **Features**: Complex bid requirements, large-scale projects

### Jaggaer
- **URL**: Varies by city
- **Authentication**: Multiple city accounts
- **Coverage**: Vancouver, Victoria, Ontario
- **Sync Frequency**: Daily recommended
- **Credentials**: Multiple accounts per city
- **Features**: City-specific procurement

### City Portals
- **Cities**: Vancouver, Chilliwack, Victoria, New Westminster, Metro Vancouver, Capital Regional District
- **Authentication**: City-specific credentials
- **Coverage**: Municipal opportunities
- **Sync Frequency**: Daily recommended
- **Features**: City-specific procurement portals

## US Data Sources

### SAM.gov
- **URL**: https://sam.gov
- **Authentication**: API key required
- **Coverage**: All US federal agencies
- **Sync Frequency**: Daily recommended
- **API Key**: Stored in environment variables
- **Features**: Federal contracting opportunities

### Federal Register
- **URL**: https://www.federalregister.gov
- **Authentication**: Public API (no auth)
- **Coverage**: Federal regulatory notices
- **Sync Frequency**: Hourly recommended
- **Features**: Regulatory opportunities and notices

### Grants.gov
- **URL**: https://www.grants.gov
- **Authentication**: API key required
- **Coverage**: Federal grant programs
- **Sync Frequency**: Daily recommended
- **Features**: Grant opportunities

## International Data Sources

### World Bank
- **URL**: https://www.worldbank.org
- **Authentication**: Public API (no auth)
- **Coverage**: Global development projects
- **Sync Frequency**: Weekly recommended
- **Features**: International infrastructure projects

## Configuration Steps

1. Navigate to **Data Integrations** tab
2. Select data source
3. Configure authentication credentials
4. Set sync frequency
5. Configure filters (geographic, service line, keywords)
6. Enable data source
7. Test sync

## Troubleshooting

### Authentication Failures
- Verify credentials in Keepass database
- Check environment variables
- Verify API keys are valid
- Check for credential expiration

### Sync Failures
- Check data source availability
- Verify network connectivity
- Review error logs
- Retry sync operation

## Related Articles

- [Data Integrations Tab](#rfp-integrations-tab)
- [Credential Management](#rfp-credentials)
- [Sync Management](#rfp-sync-management)
    `,
  },
  {
    id: 'rfp-environment-configuration',
    title: 'Environment Configuration Reference',
    category: 'admin' as HelpCategory,
    summary: 'Reference guide for Keystone on-premise environment configuration including database, Redis, storage, and Azure services',
    keywords: ['environment', 'configuration', 'database', 'redis', 'storage', 'azure', 'on-premise'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Environment Configuration Reference

Reference guide for Keystone on-premise environment configuration, including database, Redis, storage, and Azure services.

## Database Configuration

### PostgreSQL
- **Host**: 10.0.245.32
- **Port**: 5432
- **Database**: traceos_keystone
- **User**: keystoneadmin
- **SSL Mode**: prefer
- **Connection**: On-premise network

## Redis Configuration

### Redis Cache
- **Host**: 10.0.245.32
- **Port**: 6379
- **Password**: Configured in environment
- **SSL**: Disabled
- **Connection**: On-premise network

## Storage Configuration

### File System Storage
- **Provider**: filesystem
- **Path**: C:\\temp\\keystone-storage
- **Type**: Local file system
- **Access**: On-premise server

## Azure Services

### Azure OpenAI
- **Endpoint**: keystone-rfp-openai-canada-east.openai.azure.com
- **API Version**: 2024-12-01-preview
- **LLM Deployment**: gpt-4.1
- **Fallback Deployment**: gpt-4.1-mini
- **Embedding Deployment**: text-embedding-3-large
- **Dimension**: 1536

### Azure Computer Vision
- **Endpoint**: traceos-vision-keystone.cognitiveservices.azure.com
- **Purpose**: OCR and document processing
- **Access**: API key authentication

## Environment Variables

### Database Variables
\`\`\`
POSTGRES_HOST=10.0.245.32
POSTGRES_PORT=5432
POSTGRES_DB=traceos_keystone
POSTGRES_USER=keystoneadmin
POSTGRES_SSLMODE=prefer
\`\`\`

### Redis Variables
\`\`\`
REDIS_HOST=10.0.245.32
REDIS_PORT=6379
REDIS_SSL_ENABLED=false
\`\`\`

### Storage Variables
\`\`\`
STORAGE_PROVIDER=filesystem
STORAGE_PATH=C:\\temp\\keystone-storage
\`\`\`

### Azure Variables
\`\`\`
AZURE_OPENAI_ENDPOINT=https://keystone-rfp-openai-canada-east.openai.azure.com/
AZURE_OPENAI_API_VERSION=2024-12-01-preview
AZURE_LLM_DEPLOYMENT=gpt-4.1
AZURE_EMBEDDING_DEPLOYMENT=text-embedding-3-large
\`\`\`

## Configuration Files

### Launch Configuration
- Location: .vscode/launch.json
- Configuration: Keystone On-Premise environment
- Purpose: Development and debugging

### Infrastructure Credentials
- Location: TraceOS/docs/keystone-rfp-agent/deployment/INFRASTRUCTURE_CREDENTIALS.txt
- Purpose: Deployment credentials reference
- Security: Keep secure, do not commit

## Related Articles

- [Credential Management](#rfp-credentials)
- [Data Source Integrations](#rfp-data-source-integrations-detailed)
    `,
  },
  {
    id: 'rfp-knowledge-base-overview',
    title: 'Knowledge Base Overview',
    category: 'knowledge-base' as HelpCategory,
    summary: 'Overview of the comprehensive knowledge base with 15,000 documents, 90,000 entities, and 200,000 searchable chunks',
    keywords: ['knowledge-base', 'documents', 'entities', 'chunks', 'search', 'rag'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Knowledge Base Overview

The RFP Agent knowledge base is a comprehensive repository of 15,000 documents transformed into 90,000 structured entities and 200,000 searchable chunks, powering AI-enhanced proposal creation.

## Knowledge Base Statistics

### Documents
- **Total Documents**: 15,000 documents
- **Document Types**: Proposals, work plans, project documents, RFP responses, technical documentation
- **Processing Status**: 100% complete
- **Quality**: High-quality, relevant content

### Entities
- **Total Entities**: 90,000 structured entities
- **Entity Types**: 20+ distinct entity types
- **Coverage**: Comprehensive coverage of Keystone's knowledge domain
- **Quality**: High-quality, validated entities

### Chunks
- **Total Chunks**: 200,000 searchable chunks
- **Chunk Size**: Optimized for semantic search
- **Coverage**: Full document coverage
- **Quality**: High-quality, context-preserving chunks

## Entity Types

### Government Opportunities
- Tenders, RFPs, solicitations
- Procurement notices
- Opportunity metadata

### Internal Documents
- Proposals, work plans
- Project documents
- RFP responses

### Company Intelligence
- Staff members, qualifications
- Certifications, past projects
- Clients, capabilities

### Opportunity Analysis
- Requirements, compliance items
- Match scores, feasibility assessments

### Evidence & Compliance
- Evidence entries, citations
- Policy compliance tracking

### Project Execution
- Technical approaches
- Methodologies, project plans

## Knowledge Base Architecture

### Hybrid Search
- **Vector Search**: Semantic similarity search
- **Keyword Search**: BM25-based keyword matching
- **Hybrid Retrieval**: Combined vector + keyword with RRF

### Citation System
- Document-level citations
- Page-level citations
- Source traceability
- Evidence chains

## Using the Knowledge Base

### In Proposal Generation
- Automatic evidence retrieval
- Citation generation
- Source traceability
- Quality assurance

### In Search
- Semantic search across documents
- Entity-based queries
- Hybrid search results
- Relevance ranking

## Performance

- **Query Latency**: <1.5 seconds average
- **Retrieval Accuracy**: High relevance scores
- **Scalability**: Handles concurrent queries
- **Reliability**: 100% uptime

## Related Articles

- [AI-Powered Proposal Generation](#rfp-ai-proposal-generation)
- [Citation & Reference System](#rfp-citation-reference-system)
- [Proposal Search Tab](#rfp-proposal-search-tab)
    `,
  },
  {
    id: 'rfp-troubleshooting-guide',
    title: 'Troubleshooting Guide',
    category: 'troubleshooting' as HelpCategory,
    summary: 'Common issues and solutions for RFP Agent including sync failures, authentication errors, and proposal generation issues',
    keywords: ['troubleshooting', 'issues', 'errors', 'sync', 'authentication', 'generation'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# Troubleshooting Guide

Common issues and solutions for RFP Agent, including sync failures, authentication errors, and proposal generation issues.

## Sync Issues

### Sync Failures
**Problem**: Data source sync fails
**Solutions**:
1. Check data source availability
2. Verify network connectivity
3. Review credentials in Keepass
4. Check environment variables
5. Review error logs
6. Retry sync operation

### Slow Sync
**Problem**: Sync operations are slow
**Solutions**:
1. Reduce sync scope (use filters)
2. Increase sync frequency (smaller batches)
3. Check network performance
4. Review data source performance

## Authentication Issues

### Credential Failures
**Problem**: Authentication fails for data source
**Solutions**:
1. Verify credentials in Keepass database
2. Check environment variables
3. Verify API keys are valid
4. Check for credential expiration
5. Update credentials if needed
6. Test authentication manually

### API Key Issues
**Problem**: API key authentication fails
**Solutions**:
1. Verify API key is correct
2. Check API key expiration
3. Verify API key permissions
4. Regenerate API key if needed

## Proposal Generation Issues

### Generation Failures
**Problem**: Proposal generation fails
**Solutions**:
1. Check opportunity document is indexed
2. Verify knowledge base has relevant content
3. Check AI service availability
4. Review error messages
5. Retry generation

### Poor Quality Content
**Problem**: Generated content quality is poor
**Solutions**:
1. Review evidence sources
2. Check citation coverage
3. Verify knowledge base content
4. Adjust generation parameters
5. Use section editing to improve

## Citation Issues

### Missing Citations
**Problem**: Generated content lacks citations
**Solutions**:
1. Verify evidence retrieval is working
2. Check knowledge base has relevant documents
3. Review citation settings
4. Regenerate with citation requirements

### Incorrect Citations
**Problem**: Citations are incorrect
**Solutions**:
1. Verify source documents
2. Check citation format
3. Review evidence retrieval
4. Update citations manually if needed

## Staff Matching Issues

### No Matches Found
**Problem**: Staff matching returns no matches
**Solutions**:
1. Review requirement criteria
2. Check staff data is complete
3. Adjust matching rules
4. Lower match score threshold
5. Verify staff designations

### Incorrect Match Scores
**Problem**: Match scores seem incorrect
**Solutions**:
1. Review matching rules
2. Check rule weights
3. Verify rule conditions
4. Review calculation steps
5. Adjust rules as needed

## Performance Issues

### Slow Page Loading
**Problem**: Pages load slowly
**Solutions**:
1. Check network connectivity
2. Review server performance
3. Clear browser cache
4. Reduce data scope (use filters)
5. Check database performance

### Timeout Errors
**Problem**: Operations timeout
**Solutions**:
1. Increase timeout settings
2. Reduce operation scope
3. Check server resources
4. Review network performance

## Getting Help

### Support Resources
- Review help articles
- Check error logs
- Contact system administrator
- Review documentation

### Error Reporting
- Document error messages
- Note steps to reproduce
- Include relevant context
- Report to support team

## Related Articles

- [Credential Management](#rfp-credentials)
- [Sync Management](#rfp-sync-management)
- [Data Source Integrations](#rfp-data-source-integrations-detailed)
    `,
  },
  {
    id: 'rfp-end-to-end-full-demo',
    title: 'End-to-End: Full Product Demo',
    category: 'getting-started' as HelpCategory,
    summary: 'Complete walkthrough of RFP Agent from opportunity discovery through proposal submission',
    keywords: ['demo', 'walkthrough', 'end-to-end', 'complete', 'workflow', 'tutorial'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    priority: 90, // Very high priority
    subcategory: 'End-to-End Workflows',
    content: `
# End-to-End: Full Product Demo

Complete walkthrough demonstrating the full RFP Agent workflow from opportunity discovery through proposal submission.

## Demo Overview

This comprehensive demo showcases how RFP Agent transforms the proposal creation process, reducing time from 60 hours to 36-42 hours while improving quality and compliance.

## Step 1: Opportunity Discovery (5 minutes)

### Navigate to Government Data Dashboard
1. Open **Government Data Dashboard** from main menu
2. Select **Canada** zone for Canadian opportunities
3. Go to **Opportunities** tab

### Find Relevant Opportunity
1. Use search: "Contaminated Sites Assessment"
2. Apply filters:
   - Service Line: Contaminated Sites
   - Province: BC
   - Status: Active
3. Review opportunity cards
4. Click opportunity for detailed view

### AI-Powered Opportunity Summary
1. View AI-generated summary (generated in <5 seconds)
2. Review key points:
   - Requirements overview
   - Relevant Keystone capabilities
   - Match confidence score
   - Feasibility indicators

**Time Saved**: Previously 1-2 hours manual reading → Now 5 minutes

## Step 2: Feasibility Assessment (2 minutes)

### Assess Opportunity
1. Click "Assess Feasibility" button
2. Review feasibility score (0-100%)
3. Examine detailed analysis:
   - Requirement-by-requirement breakdown
   - Capability match summary
   - Risk factors
   - Recommendations

### Make Go/No-Go Decision
- Review overall score and recommendations
- Check capability gaps
- Evaluate risk factors
- Make informed decision

**Time Saved**: Previously 2-3 hours manual analysis → Now 2 minutes

## Step 3: Staff Matching (3 minutes)

### Match Staff to Requirements
1. Navigate to **Staff Matching Dashboard**
2. System automatically extracts requirements from RFP
3. Review match results:
   - Top matches with scores
   - Status indicators (matched/partial/no match)
   - Calculation steps
4. Select staff for proposal team

**Time Saved**: Previously 1-2 hours manual review → Now 3 minutes

## Step 4: Proposal Template Generation (30 seconds)

### Generate Template
1. Click "Generate Proposal" from opportunity
2. Proposal Builder modal opens
3. Review opportunity and client information
4. Click "Generate Template"
5. Template generated in 10-30 seconds

### Review Template Structure
- Sections tailored to opportunity
- Relevant questions per section
- Evidence source mapping
- Policy compliance validation

**Time Saved**: Previously 2-3 hours manual template creation → Now 30 seconds

## Step 5: Proposal Content Generation (3-5 minutes)

### Generate Proposal Sections
1. Click "Start Generation"
2. Monitor progress (streaming output)
3. System automatically:
   - Retrieves relevant evidence from 15,000 documents
   - Generates content with citations
   - Validates against requirements
   - Ensures citation coverage

### Review Generated Content
1. Review each section
2. Check citations (all claims backed by sources)
3. Verify requirement coverage
4. Review quality scores

**Time Saved**: Previously 20-30 hours manual writing → Now 3-5 minutes

## Step 6: Content Refinement (2-4 hours)

### Edit and Enhance Sections
1. Navigate to proposal editor
2. Use AI-powered editing:
   - Quick Edit: Fast improvements
   - Comprehensive Rewrite: Complete enhancement
   - Technical Enhancement: Deepen technical content
   - Compliance Review: Ensure full compliance
3. Review side-by-side comparison
4. Accept/reject changes

**Time Saved**: Previously 10-15 hours manual editing → Now 2-4 hours

## Step 7: Review and Approval (1-2 hours)

### Submit for Review
1. Navigate to **Review** tab
2. Submit proposal for senior review
3. Reviewers see RFP-specific checklist

### Complete Review Checklist
1. Reviewers complete checklist items
2. Add comments and feedback
3. Address all items
4. Obtain approval

**Time Saved**: Previously 4-6 hours manual review → Now 1-2 hours

## Step 8: Final Submission (30 minutes)

### Quality Assurance
1. Final quality check
2. Verify all requirements addressed
3. Check citation accuracy
4. Export proposal

### Submit to Client
1. Export as PDF or DOCX
2. Submit through client portal
3. Update opportunity status
4. Track submission

**Total Time**: 36-42 hours (vs. 60 hours previously)
**Time Saved**: 18-24 hours per proposal
**Cost Savings**: $3,258-$4,344 per proposal (@ $181/hr)

## Key Benefits Demonstrated

- **Speed**: 30-50% time reduction
- **Quality**: Evidence-based with full citations
- **Compliance**: Built-in validation
- **Consistency**: Standardized structure
- **Traceability**: Full source attribution

## Related Articles

- [Proposal Generation Workflow](#rfp-proposal-generation-workflow)
- [AI-Powered Proposal Generation](#rfp-ai-proposal-generation)
- [Review Workflow Tab](#rfp-review-workflow-tab)
    `,
  },
  {
    id: 'rfp-end-to-end-search-bar',
    title: 'End-to-End: Search Bar Workflow',
    category: 'getting-started' as HelpCategory,
    summary: 'Complete workflow using the search functionality to find opportunities, proposals, and content',
    keywords: ['search', 'workflow', 'opportunities', 'proposals', 'content', 'discovery'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# End-to-End: Search Bar Workflow

Complete workflow demonstrating how to use search functionality across RFP Agent to find opportunities, proposals, and relevant content.

## Search Overview

RFP Agent provides powerful search capabilities across:
- Government opportunities
- Past proposals
- Knowledge base documents
- Staff members
- Requirements and capabilities

## Workflow: Finding Opportunities

### Step 1: Search Opportunities (2 minutes)

1. Navigate to **Government Data Dashboard** > **Opportunities** tab
2. Enter search query: "environmental assessment BC"
3. AI suggests keywords:
   - "Environmental Impact Assessment"
   - "Site Assessment"
   - "Contaminated Sites"
4. Click suggested keyword to refine search
5. Review search results

**Value**: Find relevant opportunities in seconds vs. hours of manual browsing

### Step 2: Filter and Refine (1 minute)

1. Apply filters:
   - Service Line: Contaminated Sites
   - Province: BC
   - Deadline: Next 30 days
2. Review filtered results
3. Click opportunity for details

**Value**: Precise filtering saves time finding exactly what you need

### Step 3: View Opportunity Summary (30 seconds)

1. AI generates comprehensive summary automatically
2. Review:
   - Key requirements
   - Relevant capabilities
   - Match confidence
   - Feasibility indicators

**Value**: Understand opportunity in 30 seconds vs. 1-2 hours reading

## Workflow: Finding Past Proposals

### Step 1: Search Past Proposals (1 minute)

1. Navigate to **Proposal Management Dashboard** > **Search** tab
2. Enter search: "contaminated sites remediation Vancouver"
3. View search results in grid/list view
4. Filter by:
   - Win status: Won
   - Service line: Contaminated Sites
   - Date: Last 2 years

**Value**: Find relevant past proposals instantly vs. hours in SharePoint

### Step 2: Compare Proposals (2 minutes)

1. Select multiple proposals
2. Click "Compare" button
3. View side-by-side comparison
4. Identify best practices
5. Add to evidence basket

**Value**: Learn from winning proposals quickly

### Step 3: Use in New Proposal (1 minute)

1. Evidence basket items automatically considered
2. Citations include past proposal references
3. Reuse proven content

**Value**: Leverage past success in new proposals

## Workflow: Knowledge Base Search

### Step 1: Search Knowledge Base (30 seconds)

1. Use search in any dashboard
2. Enter query: "CSAP designation requirements"
3. Results from 15,000 documents
4. View relevant chunks with citations

**Value**: Find information across entire knowledge base instantly

### Step 2: Review Evidence (1 minute)

1. Click citation to view source document
2. Review page-level context
3. Verify information accuracy
4. Use in proposal

**Value**: Evidence-based content with full traceability

## Search Features

### Semantic Search
- Natural language queries
- AI-powered relevance
- Context-aware results

### Keyword Suggestions
- AI suggests relevant keywords
- Based on search history
- Opportunity-based suggestions

### Advanced Filtering
- Multiple filter options
- Combined filters
- Saved searches

## Time Savings

- **Opportunity Discovery**: 1-2 hours → 2-3 minutes
- **Past Proposal Research**: 2-3 hours → 1-2 minutes
- **Knowledge Base Search**: 1-2 hours → 30 seconds
- **Total Saved**: 4-7 hours per proposal

## Related Articles

- [Proposal Search Tab](#rfp-proposal-search-tab)
- [Opportunities Tab](#rfp-opportunities-tab)
- [AI-Powered Keyword Suggestions](#rfp-ai-keyword-suggestions)
    `,
  },
  {
    id: 'rfp-end-to-end-proposal-creation',
    title: 'End-to-End: Proposal Creation Workflow',
    category: 'workflows' as HelpCategory,
    summary: 'Complete workflow for creating a proposal from opportunity selection to final submission',
    keywords: ['proposal', 'creation', 'workflow', 'generation', 'submission', 'end-to-end'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    priority: 90, // Very high priority
    subcategory: 'End-to-End Workflows',
    content: `
# End-to-End: Proposal Creation Workflow

Complete step-by-step workflow for creating a proposal from opportunity selection through final submission.

## Workflow Overview

**Previous Process**: 60 hours manual work
**With RFP Agent**: 36-42 hours (30-50% reduction)
**Time Saved**: 18-24 hours per proposal

## Step 1: Opportunity Selection (5 minutes)

### Find and Select Opportunity
1. Navigate to **Government Data Dashboard**
2. Search and filter opportunities
3. Review AI-generated summary
4. Assess feasibility
5. Select opportunity for proposal

**Previous**: 1-2 hours browsing and reading
**Now**: 5 minutes with AI summaries

## Step 2: Staff Matching (3 minutes)

### Match Staff to Requirements
1. Navigate to **Staff Matching Dashboard**
2. System extracts requirements automatically
3. Review match results with scores
4. Select qualified staff members
5. Verify designations and availability

**Previous**: 1-2 hours manual review
**Now**: 3 minutes automated matching

## Step 3: Template Generation (30 seconds)

### Generate Proposal Template
1. Click "Generate Proposal" from opportunity
2. Proposal Builder opens
3. Review opportunity details
4. Click "Generate Template"
5. Template ready in 10-30 seconds

**Previous**: 2-3 hours manual template creation
**Now**: 30 seconds automated generation

## Step 4: Content Generation (3-5 minutes)

### Generate Proposal Content
1. Click "Start Generation"
2. Monitor progress (streaming output)
3. System automatically:
   - Retrieves evidence from 15,000 documents
   - Generates sections with citations
   - Validates against requirements
4. Review generated content

**Previous**: 20-30 hours manual writing
**Now**: 3-5 minutes AI generation

## Step 5: Content Refinement (2-4 hours)

### Edit and Enhance
1. Review each section
2. Use AI editing features:
   - Quick Edit for fast improvements
   - Comprehensive Rewrite for enhancement
   - Technical Enhancement for depth
3. Verify citations
4. Ensure requirement coverage

**Previous**: 10-15 hours manual editing
**Now**: 2-4 hours with AI assistance

## Step 6: Review Process (1-2 hours)

### Submit for Review
1. Navigate to **Review** tab
2. Submit proposal
3. Reviewers complete checklist
4. Address feedback
5. Obtain approval

**Previous**: 4-6 hours manual review
**Now**: 1-2 hours streamlined process

## Step 7: Final Submission (30 minutes)

### Quality Check and Submit
1. Final quality review
2. Verify all requirements
3. Check citations
4. Export proposal
5. Submit to client

**Previous**: 2-3 hours final preparation
**Now**: 30 minutes with validation

## Total Time Comparison

| Task | Previous | With RFP Agent | Time Saved |
|------|----------|----------------|------------|
| Opportunity Analysis | 1-2 hrs | 5 min | 1-2 hrs |
| Staff Matching | 1-2 hrs | 3 min | 1-2 hrs |
| Template Creation | 2-3 hrs | 30 sec | 2-3 hrs |
| Content Writing | 20-30 hrs | 3-5 min | 20-30 hrs |
| Content Editing | 10-15 hrs | 2-4 hrs | 6-11 hrs |
| Review Process | 4-6 hrs | 1-2 hrs | 2-4 hrs |
| Final Preparation | 2-3 hrs | 30 min | 1.5-2.5 hrs |
| **Total** | **60 hrs** | **36-42 hrs** | **18-24 hrs** |

## Cost Savings

- **Time Saved**: 18-24 hours per proposal
- **Rate**: $181/hour (average PM billing rate)
- **Savings**: $3,258-$4,344 per proposal
- **Annual Impact**: 40 proposals × $4,000 = **$160,000 savings**

## Quality Improvements

- **Evidence-Based**: All claims backed by source documents
- **Full Citations**: Document and page-level citations
- **Compliance**: Built-in validation
- **Consistency**: Standardized structure
- **Traceability**: Complete audit trail

## Related Articles

- [Proposal Generation Workflow](#rfp-proposal-generation-workflow)
- [AI-Powered Proposal Generation](#rfp-ai-proposal-generation)
- [Review Workflow Tab](#rfp-review-workflow-tab)
    `,
  },
  {
    id: 'rfp-end-to-end-onboarding-rbac',
    title: 'End-to-End: Onboarding & RBAC Setup',
    category: 'admin' as HelpCategory,
    summary: 'Complete workflow for onboarding new users and configuring role-based access control',
    keywords: ['onboarding', 'rbac', 'users', 'roles', 'permissions', 'access', 'setup'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# End-to-End: Onboarding & RBAC Setup

Complete workflow for onboarding new users and configuring role-based access control (RBAC) in RFP Agent.

## RBAC Overview

RFP Agent implements comprehensive role-based access control to ensure secure, appropriate access to system features and data based on user roles and responsibilities.

## User Roles

### Administrator
- Full system access
- User management
- Configuration management
- System administration

### Proposal Manager
- Full proposal creation and management
- Team coordination
- Review assignment
- Proposal approval

### Proposal Writer
- Proposal creation and editing
- Content generation
- Section editing
- Draft management

### Reviewer
- Review and approval access
- Checklist completion
- Comment and feedback
- Approval decisions

### Viewer
- Read-only access
- Proposal viewing
- Opportunity viewing
- Report viewing

### Data Manager
- Data source management
- Integration configuration
- Sync management
- Knowledge base management

## Onboarding Workflow

### Step 1: Create User Account (2 minutes)

1. Administrator navigates to user management
2. Click "Create User"
3. Enter user information:
   - Name and email
   - Department
   - Contact information
4. Assign initial role
5. Set user status (active)

**Value**: Centralized user management vs. manual account creation

### Step 2: Assign Roles (1 minute)

1. Select user
2. Assign appropriate role(s)
3. Configure role-specific permissions
4. Set access levels
5. Save configuration

**Value**: Granular access control ensures security

### Step 3: User Login and Setup (5 minutes)

1. User receives login credentials
2. First login prompts password change
3. Profile configuration
4. Preferences setup
5. Dashboard tour

**Value**: Quick onboarding with guided setup

### Step 4: Initial Training (30 minutes)

1. System overview training
2. Role-specific feature training
3. Basic workflow training
4. Best practices introduction
5. Q&A session

**Value**: Comprehensive training ensures effective use

## RBAC Configuration

### Permission Levels

#### Feature-Level Access
- Access to specific features
- Tab-level permissions
- Page-level restrictions

#### Data-Level Access
- Access to specific data
- Project-based access
- Document-level permissions

#### Action-Level Access
- Create, read, update, delete
- Approve, reject
- Export, share

### Permission Configuration

1. Navigate to role management
2. Select role to configure
3. Set feature permissions
4. Configure data access
5. Set action permissions
6. Save configuration

## Security Features

### Access Control
- Role-based feature access
- Data-level restrictions
- Action-level validation
- Audit logging

### Audit Trail
- All access attempts logged
- Permission changes tracked
- User activity monitoring
- Compliance reporting

## Best Practices

### Role Assignment
- Assign minimum required permissions
- Review role assignments regularly
- Update roles as responsibilities change
- Document role purposes

### User Management
- Onboard users promptly
- Provide comprehensive training
- Monitor user activity
- Deactivate unused accounts

### Security
- Regular access reviews
- Permission audits
- Security monitoring
- Compliance validation

## Time Savings

- **User Creation**: 5 minutes vs. 30 minutes manual
- **Role Configuration**: 1 minute vs. 15 minutes manual
- **Training**: 30 minutes structured vs. ad-hoc
- **Total**: 36 minutes vs. 45+ minutes

## Related Articles

- [Proposal Management Dashboard](#rfp-proposal-management-dashboard)
- [Government Data Dashboard](#rfp-government-data-dashboard)
    `,
  },
  {
    id: 'rfp-end-to-end-integrations-data-mgmt',
    title: 'End-to-End: Integrations & Data Management',
    category: 'sources' as HelpCategory,
    summary: 'Complete workflow for setting up data source integrations and managing knowledge base',
    keywords: ['integrations', 'data', 'management', 'sources', 'sync', 'knowledge-base', 'setup'],
    lastUpdated: '2026-01-15',
    plugin: 'rfp-agent',
    content: `
# End-to-End: Integrations & Data Management

Complete workflow for setting up data source integrations and managing the knowledge base in RFP Agent.

## Overview

RFP Agent integrates with 15+ data sources and manages a comprehensive knowledge base of 15,000 documents, 90,000 entities, and 200,000 searchable chunks.

## Integration Setup Workflow

### Step 1: Configure Data Source (5 minutes)

1. Navigate to **Government Data Dashboard** > **Data Integrations** tab
2. Select data source (e.g., BC Bid)
3. Click "Configure"
4. Enter credentials:
   - Username/password from Keepass
   - API keys if required
5. Test authentication
6. Save configuration

**Previous**: Manual credential management, no centralization
**Now**: Centralized, secure credential management

### Step 2: Configure Sync Settings (3 minutes)

1. Set sync frequency:
   - Real-time, hourly, daily, weekly
2. Configure filters:
   - Geographic filters (provinces, cities)
   - Service line filters
   - Keyword filters
3. Set sync scope:
   - Full sync or incremental
4. Enable data source

**Previous**: Manual opportunity checking
**Now**: Automated sync with filtering

### Step 3: Initial Sync (10-30 minutes)

1. Click "Sync Now" for initial sync
2. Monitor sync progress
3. Review sync results:
   - Opportunities synced count
   - Errors if any
4. Verify data quality

**Previous**: Hours of manual data entry
**Now**: Automated sync in minutes

### Step 4: Monitor and Maintain (Ongoing)

1. Monitor sync status
2. Review sync history
3. Address errors promptly
4. Update credentials as needed
5. Adjust filters based on needs

**Previous**: Ongoing manual monitoring
**Now**: Automated monitoring with alerts

## Knowledge Base Management

### Document Ingestion

1. Upload documents to system
2. System automatically:
   - Parses document content
   - Creates chunks for search
   - Extracts entities
   - Generates summaries
3. Documents indexed and searchable

**Value**: 15,000 documents searchable in seconds

### Entity Extraction

1. System extracts entities automatically:
   - Government opportunities
   - Staff members
   - Projects and clients
   - Requirements and capabilities
2. Creates relationships
3. Builds knowledge graph

**Value**: 90,000 structured entities for precise queries

### Chunk Creation

1. Documents chunked optimally
2. Chunks embedded for semantic search
3. Indexed for keyword search
4. Ready for hybrid retrieval

**Value**: 200,000 searchable chunks with citations

## Data Source Management

### Multiple Data Sources

#### Canadian Sources
- BC Bid, Bids&Tender, Bonfire
- Merx, SAP Ariba, Jaggaer
- City Portals (Vancouver, Victoria, etc.)

#### US Sources
- SAM.gov, Federal Register
- Grants.gov, USA Spending

#### International Sources
- World Bank

### Zone Management

1. Switch between zones (Canada/USA/Global)
2. Zone-specific data sources
3. Zone-based filtering
4. Unified opportunity view

**Value**: Single interface for all data sources

## Sync Management

### Automated Sync
- Scheduled sync operations
- Background processing
- Error handling and retry
- Status monitoring

### Manual Sync
- On-demand sync trigger
- Full or incremental sync
- Immediate execution
- Status feedback

**Value**: Always up-to-date opportunity data

## Time Savings

- **Integration Setup**: 8 minutes vs. 2-4 hours manual
- **Initial Sync**: 10-30 minutes vs. days of data entry
- **Ongoing Maintenance**: Automated vs. hours weekly
- **Total**: Significant time savings on data management

## Value Delivered

- **850+ opportunities** automatically synced
- **15,000 documents** searchable
- **90,000 entities** for precise queries
- **200,000 chunks** for comprehensive search
- **Real-time updates** from all sources

## Related Articles

- [Data Integrations Tab](#rfp-integrations-tab)
- [Data Source Integrations](#rfp-data-source-integrations-detailed)
- [Sync Management](#rfp-sync-management)
- [Knowledge Base Overview](#rfp-knowledge-base-overview)
    `,
  },
];

